// src/components/block/liveStreamManagement/MultiviewPanelV2.tsx
// v2.9 — Corrections grille intercomm :
//         - Grille écrans : gridAutoRows 1fr (plus d'espace entre les 2 lignes)
//         - screensStyle : hauteur toujours 100% (pas d'aspectRatio externe)
//         - Clic sur écran → zoom Box + ClonedVideoPlayer (comme retour HLS)

import { WWW_URLS } from '../../../backendServices/urls';
import React, { useEffect, useRef, useCallback, useState, useMemo, useContext, useLayoutEffect } from 'react';
import { useStreamDeckControl } from '../../../dataTypes/liveStreamManagment/streamDeckBridge';
import ReactDOM from 'react-dom';
import { PortalTargetContext, usePortalTarget, KleonLogo } from './DetachedWindow';
import { DetachedChatFeed } from './DetachedChatFeed';
import { intercomLevelStore } from '../../../dataTypes/liveStreamManagment/audioPipeline/audioLevelStore';
import { useAudioLevelEffect } from '../../../hooks/liveStreamManagment/useAudioLevel';
import { VideoOff, Volume2, VolumeX, ExternalLink, Settings, LayoutGrid, Cast, RefreshCw, Phone, PhoneOff, MapPin, Activity } from 'lucide-react';
import {
  subscribeBandwidth, getLastBandwidthReading, formatBps, formatBytes,
  readInternetEstimate, mesurerDebitInternet, mesurerDebitVersCast, logIcePairs,
  BandwidthReading, IcePairReport,
} from '../../../backendServices/liveStreamManagment/api_bandwidth';
import { setMetricsEnabled, isMetricsEnabled } from '../../../backendServices/debugManagment/remoteLogger';
import type { MapMarker } from '../../widget/dataDisplay/maps/OpenStreetMapV2';
import { DeviceGeoReport } from '../../../dataTypes/liveStreamManagment/types_media';
import { VideoSource, LayoutSlot, SLOT_COUNT } from '../../../contexts/MediaStreamContext';
import { makeReorderDragHandlers } from '../../../dataTypes/liveStreamManagment/utils_dragReorder';
import { SourceAliasMap, resolveSourceLabel } from './SourceAliasConfigModal';
import SourceSelector from '../broadcastingChannelManagment/SourceSelector';
import HlsAudioPanel from './HlsAudioPanel';
import MonitorVideoPlayer from '../../widget/players/MonitorVideoPlayer';
import ClonedVideoPlayer from '../../widget/players/ClonedVideoPlayer';
import Box             from '../../widget/windows/Box';

import { getHeartBeatCount } from '../../../backendServices/customerFrontEventManagment/api';

import { BroadcastingChannel } from '../../../dataTypes/broadcastingManagment/types';

import { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';
import { RemoteDevice } from '../../../dataTypes/liveStreamManagment/types_webrtc';

// maplibre-gl est lourd et instancie un contexte WebGL : on ne le charge que si
// l'opérateur ouvre l'onglet Carte. Import de TYPE séparé — il s'efface à la
// compilation et n'entraîne donc pas le module avec lui.
const OpenStreetMapV2 = React.lazy(() => import('../../widget/dataDisplay/maps/OpenStreetMapV2'));


// ─── Types exportés ───────────────────────────────────────────────────────────

export interface MultiviewSlotConfig {
  sourceId    : string | null;
  customLabel : string | null;
}

function sortSlotsByType(slots: (VideoSource | null)[]): (VideoSource | null)[] {
  const ORDER: Record<string, number> = { device: 0, screen: 1, url: 2, remote: 3 };
  const filled = slots.filter((s): s is VideoSource => s !== null);
  filled.sort((a, b) => (ORDER[a.type] ?? 99) - (ORDER[b.type] ?? 99));
  const result: (VideoSource | null)[] = [...filled];
  while (result.length < SLOT_COUNT) result.push(null);
  return result.slice(0, SLOT_COUNT);
}

export function defaultSlotConfigs(slots: (VideoSource | null)[]): MultiviewSlotConfig[] {
  const sorted = sortSlotsByType(slots);
  return Array.from({ length: SLOT_COUNT }, (_, i) => ({
    sourceId    : sorted[i]?.id ?? null,
    customLabel : null,
  }));
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface MultiviewPanelV2Props {
  /**
   * Façade `{serveur}/{uuid}/regie` du microservice de la chaîne — seule voie
   * vers son endpoint de qualité de lien, qui dit ce que MediaMTX REÇOIT.
   */
  microserviceUrl?: string;
  canvasPreviewRef          : React.RefObject<HTMLVideoElement>;
  isStreaming               : boolean;
  deckPreviewVideoRef       : React.RefObject<HTMLVideoElement>;
  deckPreviewLabel          : string;
  deckPreviewSourceId       : string | null;
  activeJingleId            : string | null;
  previewMuted              : boolean;
  onTogglePreviewMuted      : () => void;
  finalMuted         : boolean;
  onToggleFinalMuted : () => void;
  deckSlots                 : (VideoSource | null)[];
  previewStreams             : Map<string, MediaStream>;
  onDeckPreview             : (source: VideoSource) => void;
  /** Double-clic sur une mini-vue : la source part directement au flux final. */
  onDeckSwitch?             : (source: VideoSource, forceNewScreen?: boolean) => void;
  onAcquirePreview          : (source: VideoSource) => Promise<void>;
  selectedVideoSource       : string;
  channelActive             : boolean;
  /** Emplacements éteints au bouton Power du deck — la mosaïque doit les respecter. */
  poweredOffSlots?          : Set<string>;
  availableSources          : VideoSource[];
  layoutSlots               : LayoutSlot[];
  slotConfigs               : MultiviewSlotConfig[];
  onSlotConfigsChange       : (configs: MultiviewSlotConfig[]) => void;
  /**
   * Glisser-déposer d'une mini-vue sur une autre : ÉCHANGE les deux emplacements.
   * Porte jusqu'au deck — la source et la preview suivent leur nouvel emplacement.
   */
  onSlotsReorder            : (fromIndex: number, toIndex: number) => void;
  closeOnFullscreen         : boolean;
  onToggleCloseOnFullscreen : () => void;
  onOpenFullscreen          : (closePanel: boolean) => void;
  sourceAliases             : SourceAliasMap;
  streamSource                    ?: any;
  defaultStreamSource             ?: any;
  handleStreamSourceChange        ?: (source: any) => void;
  handleDefaultStreamSourceChange ?: (source: any) => void;
  channelUuid                     ?: string;
  hlsVolume?         : number;
  hlsMuted?          : boolean;
  onHlsVolumeChange? : (v: number) => void;
  onHlsMutedChange?  : (m: boolean) => void;
  monitorHlsUrl?     : string;
  broadcastingChannel: BroadcastingChannel;
  hlsMonitorStream?: MediaStream | null;
  hlsAudioRegistry?  : AudioSourceRegistry;
  hlsAudioSourceId?  : string;
  onHlsReset?: () => void;
  remoteDevices?     : RemoteDevice[];
  localMics?         : { deviceId: string; label: string }[];
  // ── Intercomm ─────────────────────────────────────────────────────────────
  intercommMicStream?          : MediaStream | null;
  intercommDeviceStreams?      : Map<string, MediaStream>;
  intercommSpeakingDevices?    : Set<string>;
  onIntercommActivate?         : (active: boolean, selectedDevices: Set<string>) => void;
  onIntercommMicGainChange?    : (linearGain: number) => void;
  onIntercommMicChange?        : (deviceId: string) => void;
  /** Position et visée des appareils distants — onglet Carte. */
  deviceGeo?                   : Map<string, DeviceGeoReport>;
  /**
   * Abonne les appareils indiqués à l'émission de leur position. Appelé par
   * l'onglet Carte à son ouverture et à sa fermeture — cf. `CarteView`.
   */
  onGeoWatch?                  : (active: boolean, deviceIds: string[]) => void;
  /**
   * Allume la prise de mesures : relevés de débit de la régie dans son fichier,
   * qualité du lien de chaque téléphone dans le sien, CPU des process ffmpeg côté
   * serveur. Piloté par le bouton de l'onglet Débits.
   */
  onMetricsLog?                : (active: boolean, deviceIds: string[]) => void;
  programLinkActive?           : boolean;
  /** Régie pendant que le multiview est dans un autre onglet : seulement Intercomm + diffusion du flux final. */
  sideOnly?                    : boolean;
  programLinkOnAir?            : boolean;
  onCloseProgramLink?          : () => void;
  onReopenProgramLink?         : () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMOJI_RE = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\ufe0f\s]+/u;
function cleanLabel(raw: string): string {
  return raw.replace(EMOJI_RE, '').trim();
}

// Épaisseur du tally rouge « en final », en pixels. Un seul endroit à régler : la
// mosaïque et la colonne Flux final la partagent, et rien d'autre n'en dépend —
// le cadre est en surimpression, il ne change aucune dimension.
const TALLY_WIDTH = 4;

// ─── MiniScreen ───────────────────────────────────────────────────────────────

/** Délai maximal entre deux clics d'un double-clic sur une mini-vue (ms). */
const DOUBLE_CLICK_MS = 300;

interface MiniScreenProps {
  slot             : VideoSource | null;
  index            : number;
  stream           : MediaStream | undefined;
  isPreview        : boolean;
  isLive           : boolean;
  customLabel      : string | null;
  sourceAliases    : SourceAliasMap;
  onDeckPreview    : (source: VideoSource) => void;
  onDeckSwitch?    : (source: VideoSource, forceNewScreen?: boolean) => void;
  onAcquirePreview : (source: VideoSource) => Promise<void>;
  channelActive    : boolean;
  /** Glisser-déposer : réordonne les emplacements, deck compris. */
  onReorder        : (fromIndex: number, toIndex: number) => void;
  isDragged        : boolean;
  isDropTarget     : boolean;
  onDragStateChange: (dragging: number | null, over: number | null) => void;
  /** Emplacement éteint par le bouton Power du deck : ne rien acquérir, ne rien montrer. */
  isPoweredOff     : boolean;
}

const MiniScreen: React.FC<MiniScreenProps> = ({
  slot, index, stream, isPreview, isLive,
  customLabel, sourceAliases, onDeckPreview, onDeckSwitch, onAcquirePreview, channelActive,
  onReorder, isDragged, isDropTarget, onDragStateChange, isPoweredOff,
}) => {
  const videoRef     = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const acquiringRef = useRef(false);

  const resolvedLabel = slot
    ? (customLabel?.trim() || cleanLabel(resolveSourceLabel(slot, sourceAliases)))
    : null;
  const displayLabel = resolvedLabel || null;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (stream?.active) {
      if (el.srcObject !== stream) {
        el.srcObject = stream;
        el.play().catch(() => {});
      }
    } else {
      el.srcObject = null;
    }
  }, [stream]);

  // Vignette figée : le <video> d'une mini-vue peut cesser d'avancer sans qu'aucune
  // erreur ne soit levée (flux source resté vivant, lecture mise en pause par le
  // navigateur). Rien ne le détectait ni ne le disait. Immobile depuis 4 s, on relance
  // la lecture ; 4 s de plus, on recolle le flux — seulement ALORS, pour ne pas
  // retomber dans le scintillement d'une réaffectation de `srcObject` à l'identique.
  // Chaque étape est journalisée avec l'état du lecteur.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream?.active) return;
    let last = el.currentTime;
    let lastMove = Date.now();
    let tried = 0;
    const timer = setInterval(() => {
      if (el.ownerDocument.hidden || !el.srcObject) { last = el.currentTime; lastMove = Date.now(); tried = 0; return; }
      if (Math.abs(el.currentTime - last) > 0.01) { last = el.currentTime; lastMove = Date.now(); tried = 0; return; }
      const stuck = Date.now() - lastMove;
      if (tried >= 2 || stuck < 4_000 * (tried + 1)) return;
      tried++;
      const vt = stream.getVideoTracks()[0];
      console.warn(
        `[MULTIVIEW] vignette figée « ${displayLabel ?? slot?.id ?? '?'} » depuis ${Math.round(stuck / 1000)} s — palier ${tried}/2`
        + ` | paused=${el.paused} readyState=${el.readyState} piste=${vt ? `${vt.readyState}${vt.muted ? '/muette' : ''}` : 'aucune'}`,
      );
      if (tried === 1) el.play().catch(() => {});
      else { el.srcObject = null; el.srcObject = stream; el.play().catch(() => {}); }
    }, 1_500);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  // Éteindre une source au bouton Power du deck ne la faisait pas disparaître d'ici :
  // la mini-vue la ré-acquérait toute seule — à l'entrée dans le champ, puis toutes les
  // trois secondes. La caméra restait donc allumée, diode comprise, alors que l'opérateur
  // venait de l'éteindre. L'auto-emprunt ne doit jamais rallumer ce qu'on a coupé à la main.
  // Webcam locale et partage d'écran n'ont rien à voir avec la diffusion : leur mini-vue
  // n'a pas à attendre que la chaîne soit active, contrairement aux flux distants/HLS que
  // `channelActive` protège plus bas (huit flux tirés à l'ouverture sinon, cf. commentaire
  // historique). Sans cette exception, la webcam restait noire tant que la chaîne visée
  // n'était pas active — constaté le 15/09/2026 sur Lovelight TV.
  const isLocalSource = slot?.type === 'device' || slot?.type === 'screen';

  const tryAcquire = useCallback(async () => {
    // `slot.awaiting` : l'appareil est tombé et on tient sa place. Il n'y a rien à
    // emprunter, et insister remplirait le journal de « stream pas encore injecté »
    // toutes les trois secondes jusqu'à son retour.
    if (!slot || (!channelActive && !isLocalSource) || stream?.active || acquiringRef.current || isLive || isPoweredOff || slot.awaiting || slot.type === 'audio') return;
    acquiringRef.current = true;
    try { await onAcquirePreview(slot); }
    finally { acquiringRef.current = false; }
  }, [slot, stream, onAcquirePreview, channelActive, isLocalSource, isLive, isPoweredOff]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !slot) return;
    // L'observateur doit venir de la fenêtre qui porte l'élément : celui de la régie, posé sur un
    // élément d'un autre onglet, ne le voit jamais « dans le champ » et plus aucune source
    // n'était tirée (Playlist en tête, qui n'est jamais tirée d'office).
    const IO = ((el.ownerDocument.defaultView as (Window & typeof globalThis) | null) ?? window).IntersectionObserver;
    const observer = new IO(
      entries => { if (entries[0].isIntersecting) tryAcquire(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [slot, tryAcquire]);

  useEffect(() => {
    if (!slot || (!channelActive && !isLocalSource) || stream?.active || isLive || isPoweredOff || slot.awaiting || slot.type === 'audio') return;
    const tid = setTimeout(() => tryAcquire(), 3000);
    return () => clearTimeout(tid);
  }, [slot, stream, tryAcquire, channelActive, isLocalSource, isLive, isPoweredOff]);

  // Un emplacement éteint est noir, quoi qu'il reste dans le registre : le flux peut
  // survivre un instant à l'extinction (une autre vue l'emprunte encore), et la mini-vue
  // continuerait sinon d'afficher une source que le deck annonce coupée.
  const hasStream = !!(stream?.active) && !isPoweredOff;
  const hasBadge  = isLive || isPreview;

  // Le tally rouge est dessiné PAR-DESSUS l'image, pas en bordure.
  //
  // Une bordure participe à la boîte : l'épaissir sur la seule source en final
  // décalerait sa vignette d'un ou deux pixels et romprait l'alignement de la
  // mosaïque — et, sur les colonnes Preview / Flux final, celui des deux images
  // entre elles. Toutes les bordures restent donc à 1.5 px quel que soit l'état,
  // et c'est un cadre en surimpression qui porte l'épaisseur.
  // Une place tenue se voit de loin : sans liseré, une vignette en attente ne se
  // distingue pas d'un emplacement vide, et c'est précisément ce qu'on cherchait à
  // rendre visible.
  const borderColor = isLive          ? 'rgba(239,68,68,0.9)'
                    : isPreview       ? 'rgba(34,197,94,0.9)'
                    : slot?.awaiting  ? 'rgba(245,158,11,0.55)'
                    : hasStream       ? 'rgba(255,255,255,0.12)'
                    : 'rgba(255,255,255,0.04)';

  const glowShadow  = isLive    ? '0 0 0 2px rgba(239,68,68,0.5)'
                    : isPreview ? '0 0 0 2px rgba(34,197,94,0.5)'
                    : 'none';

  // Un emplacement VIDE reste une cible de dépôt valable — y déposer une source
  // revient à la déplacer — mais il n'y a rien à en tirer, d'où le draggable
  // conditionné à la présence d'une source.
  // Même mécanique que les sources du deck et la rangée Preview — c'est le même
  // tableau d'emplacements vu de trois endroits, d'où la famille `slot` partagée :
  // on peut glisser d'une vue à l'autre.
  const dragHandlers = makeReorderDragHandlers({
    family : 'slot',
    index,
    canDrag: !!slot,
    onReorder,
    onDragStateChange,
  });

  // Le clic envoie la source en preview ; le glisser la déplace. Sans ce garde,
  // relâcher un glisser sur place déclencherait aussi la preview — l'opérateur
  // changerait d'antenne en croyant n'avoir rien fait.
  // Le double-clic envoie la source au flux final, comme la rangée Final du deck.
  // Le clic simple est donc RETENU pendant DOUBLE_CLICK_MS : un second clic dans ce
  // délai l'annule et part au final. Sans cette attente, le premier clic d'un
  // double-clic passait d'abord la source en preview — impossible d'envoyer au final
  // sans toucher à la preview.
  const draggingRef   = useRef(false);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (clickTimerRef.current) clearTimeout(clickTimerRef.current); }, []);

  const handleClick = () => {
    if (!slot || draggingRef.current) return;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      onDeckSwitch?.(slot, slot.type === 'screen');
      return;
    }
    // Sans flux final branché, rien à attendre : le clic reste immédiat.
    if (!onDeckSwitch) { onDeckPreview(slot); return; }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      onDeckPreview(slot);
    }, DOUBLE_CLICK_MS);
  };

  return (
    <div
      ref={containerRef}
      {...dragHandlers}
      onDragStart={(e) => { draggingRef.current = true; dragHandlers.onDragStart(e); }}
      onDragEnd={() => { dragHandlers.onDragEnd(); setTimeout(() => { draggingRef.current = false; }, 0); }}
      onClick={handleClick}
      title={slot
        ? `Preview : ${displayLabel ?? `Slot ${index + 1}`}\nDouble-clic : flux final\nGlisser pour changer l'ordre`
        : `Slot ${index + 1} — vide`}
      style={{
        position    : 'relative',
        aspectRatio : '16 / 9',
        background  : '#000',
        borderRadius: 5,
        overflow    : 'hidden',
        cursor      : slot ? 'grab' : 'default',
        // La cible de dépôt est signalée par un liseré ambre : le bleu et le vert
        // sont déjà pris par LIVE et PRV, et l'opérateur doit voir où la vignette
        // va atterrir sans confondre avec un état d'antenne.
        border      : `1.5px solid ${isDropTarget ? 'rgba(245,158,11,0.95)' : borderColor}`,
        boxShadow   : isDropTarget ? '0 0 0 2px rgba(245,158,11,0.45)' : glowShadow,
        opacity     : isDragged ? 0.4 : 1,
        transition  : 'border-color 0.15s, box-shadow 0.15s, opacity 0.15s',
        flexShrink  : 0,
        width       : '100%',
      }}
    >
      <video
        ref={videoRef}
        autoPlay playsInline muted
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />

      {/* Les marges tiennent compte du cadre de tally, qui mord de TALLY_WIDTH px sur
          l'image : au ras du bord, le libellé et la pastille venaient toucher le liseré
          rouge d'une source en final. */}
      <div style={{
        position      : 'absolute',
        top           : TALLY_WIDTH + 3,
        left          : TALLY_WIDTH + 4,
        right         : hasBadge ? TALLY_WIDTH + 28 : TALLY_WIDTH + 4,
        display       : 'flex',
        alignItems    : 'center',
        gap           : 3,
        pointerEvents : 'none',
        overflow      : 'hidden',
        zIndex        : 1,
      }}>
        <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 800, color: '#ffffff', lineHeight: 1, flexShrink: 0 }}>
          {index + 1}
        </span>
        {displayLabel && (
          <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 700, color: '#ffffff', lineHeight: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {displayLabel}
          </span>
        )}
      </div>

      {!hasStream && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, background: '#000' }}>
          {slot?.type === 'audio'
            ? <Volume2 style={{ width: '40%', height: '40%', opacity: 0.35, color: '#22d3ee' }} />
            : <VideoOff style={{ width: '40%', height: '40%', opacity: slot?.awaiting ? 0.25 : 0.15, color: '#fff' }} />}
          {slot?.type === 'audio' && (
            <span style={{
              fontSize: 8, fontFamily: 'monospace', fontWeight: 800, letterSpacing: '0.5px',
              color: '#22d3ee', pointerEvents: 'none',
            }}>
              AUDIO SEUL
            </span>
          )}
          {/* Dire lequel des deux silences on regarde : un emplacement vide n'attend
              personne, celui-ci attend quelqu'un de nommé. */}
          {slot?.awaiting && (
            <span style={{
              fontSize: 8, fontFamily: 'monospace', fontWeight: 800, letterSpacing: '0.5px',
              color: '#f59e0b', pointerEvents: 'none',
            }}>
              EN ATTENTE
            </span>
          )}
        </div>
      )}

      {isLive && (
        <div style={{
          position     : 'absolute',
          inset        : 0,
          border       : `${TALLY_WIDTH}px solid rgba(239,68,68,0.95)`,
          borderRadius : 5,
          pointerEvents: 'none',
          zIndex       : 2,
        }} />
      )}

      {isLive && (
        <div style={{ position: 'absolute', top: TALLY_WIDTH + 2, right: TALLY_WIDTH + 2, fontSize: 6, fontWeight: 800, letterSpacing: '0.5px', color: '#fff', background: '#ef4444', borderRadius: 2, padding: '1px 3px', pointerEvents: 'none', zIndex: 3 }}>
          LIVE
        </div>
      )}
      {isPreview && !isLive && (
        <div style={{ position: 'absolute', top: TALLY_WIDTH + 2, right: TALLY_WIDTH + 2, fontSize: 6, fontWeight: 800, letterSpacing: '0.5px', color: '#fff', background: '#22c55e', borderRadius: 2, padding: '1px 3px', pointerEvents: 'none' }}>
          PRV
        </div>
      )}
    </div>
  );
};

// ─── ConfigModal ──────────────────────────────────────────────────────────────

interface ConfigModalProps {
  slotConfigs               : MultiviewSlotConfig[];
  availableSources          : VideoSource[];
  layoutSlots               : LayoutSlot[];
  sourceAliases             : SourceAliasMap;
  closeOnFullscreen         : boolean;
  onToggleCloseOnFullscreen : () => void;
  onSlotConfigsChange       : (configs: MultiviewSlotConfig[]) => void;
  onClose                   : () => void;
}

const ConfigModal: React.FC<ConfigModalProps> = ({
  slotConfigs, availableSources, layoutSlots, sourceAliases,
  closeOnFullscreen, onToggleCloseOnFullscreen,
  onSlotConfigsChange, onClose,
}) => {
  const portalTarget = usePortalTarget();
  const [localConfigs,           setLocalConfigs]           = useState<MultiviewSlotConfig[]>(slotConfigs.map(c => ({ ...c })));
  const [localCloseOnFullscreen, setLocalCloseOnFullscreen] = useState<boolean>(closeOnFullscreen);

  const handleSlotConfigChange = useCallback((i: number, cfg: MultiviewSlotConfig) => {
    setLocalConfigs(prev => prev.map((c, idx) => idx === i ? cfg : c));
  }, []);

  const handleConfirm = useCallback(() => {
    if (localCloseOnFullscreen !== closeOnFullscreen) onToggleCloseOnFullscreen();
    const next = localConfigs.map(c => ({ ...c }));
    while (next.length < SLOT_COUNT) next.push({ sourceId: null, customLabel: null });
    onSlotConfigsChange(next);
    onClose();
  }, [localConfigs, localCloseOnFullscreen, closeOnFullscreen, onToggleCloseOnFullscreen, onSlotConfigsChange, onClose]);

  const handleCancel = useCallback(() => onClose(), [onClose]);

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'rgb(15,23,42)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, width: 400, maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Settings style={{ width: 14, height: 14, color: 'rgba(148,163,184,0.7)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Configuration du multiview</span>
          </div>
          <button onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(148,163,184,0.7)', fontSize: 20, lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Fermer ce panneau à l'ouverture plein écran</span>
            <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.5)', fontFamily: 'monospace' }}>Le multiview s'ouvre dans un nouvel onglet</span>
          </div>
          <div
            onClick={() => setLocalCloseOnFullscreen(v => !v)}
            style={{ width: 36, height: 20, borderRadius: 10, background: localCloseOnFullscreen ? '#6366f1' : 'rgba(255,255,255,0.12)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}
          >
            <div style={{ position: 'absolute', top: 3, left: localCloseOnFullscreen ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 8, padding: '8px 16px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <span />
          <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(148,163,184,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Source</span>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {Array.from({ length: SLOT_COUNT }, (_, i) => {
            const cfg = localConfigs[i] ?? { sourceId: null, customLabel: null };
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', alignItems: 'center', gap: 8, padding: '7px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: 'rgba(255,255,255,0.35)' }}>{i + 1}</span>
                <select
                  value={cfg.sourceId ?? ''}
                  onChange={e => handleSlotConfigChange(i, { ...cfg, sourceId: e.target.value || null })}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 5, color: '#fff', fontSize: 11, padding: '4px 6px', fontFamily: 'monospace', cursor: 'pointer', width: '100%' }}
                >
                  <option value="" style={{ background: '#1e293b', color: '#fff' }}>— Vide —</option>
                  {availableSources.filter(s => s.type !== 'scene').length > 0 && (
                    <optgroup label="Sources vidéo" style={{ background: '#0d1526', color: '#64748b' }}>
                      {availableSources.filter(s => s.type !== 'scene').map(s => (
                        <option key={s.id} value={s.id} style={{ background: '#1e293b', color: '#fff' }}>
                          {cleanLabel(resolveSourceLabel(s, sourceAliases)) || s.id}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {layoutSlots.filter(l => l.background.type !== 'none' || l.incrustations.length > 0).length > 0 && (
                    <optgroup label="Scènes" style={{ background: '#0d1526', color: '#64748b' }}>
                      {layoutSlots
                        .filter(l => l.background.type !== 'none' || l.incrustations.length > 0)
                        .map(l => (
                          <option key={'scene-' + l.id} value={'scene-' + l.id} style={{ background: '#1e293b', color: '#fff' }}>
                            {cleanLabel(l.label) || l.id}
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={handleCancel} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#94a3b8', fontSize: 12, fontWeight: 700, padding: '6px 20px', cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={handleConfirm} style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)', borderRadius: 6, color: '#a5b4fc', fontSize: 12, fontWeight: 700, padding: '6px 20px', cursor: 'pointer' }}>
            Valider
          </button>
        </div>
      </div>
    </div>,
    portalTarget
  );
};

// ─── GroupSelector ────────────────────────────────────────────────────────────




// ─── RetourView ───────────────────────────────────────────────────────────────

interface RetourViewProps {
  streamSource                    ?: any;
  defaultStreamSource             ?: any;
  handleStreamSourceChange        ?: (source: any) => void;
  handleDefaultStreamSourceChange ?: (source: any) => void;
  videoHeight                     ?: number | null;
  videoWidth                      ?: number | null;
  simultaneousConnections         ?: number;
  hlsVolume?         : number;
  hlsMuted?          : boolean;
  onHlsVolumeChange? : (v: number) => void;
  onHlsMutedChange?  : (m: boolean) => void;
  monitorHlsUrl?     : string;
  // État de la chaîne (broadcastingChannel.active, basculé dès le clic d'activation) :
  // c'est lui qui pilote l'animation démarrage/arrêt du lecteur retour, exactement
  // comme sur le tableau de bord. Surtout pas un indicateur d'onglet affiché.
  channelActive      : boolean;
  broadcastingChannel: BroadcastingChannel;
  hlsMonitorStream?: MediaStream | null;
  hlsAudioRegistry?  : AudioSourceRegistry;
  hlsAudioSourceId?  : string;
  onHlsReset?: () => void;
}

const RetourView: React.FC<RetourViewProps> = React.memo(({
  streamSource,
  defaultStreamSource,
  handleStreamSourceChange,
  handleDefaultStreamSourceChange,
  videoHeight,
  videoWidth,
  simultaneousConnections = 0,
  hlsVolume,
  hlsMuted,
  onHlsVolumeChange,
  onHlsMutedChange,
  monitorHlsUrl,
  channelActive,
  broadcastingChannel,
  hlsMonitorStream,
  hlsAudioRegistry,
  hlsAudioSourceId,
  onHlsReset,
}) => {
  const portalTarget = usePortalTarget();
  const [tvOpen, setTvOpen] = useState(false);

  useEffect(() => {
    if (!tvOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTvOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [tvOpen]);

  return (
    <div style={{
      display             : 'grid',
      gridTemplateColumns : videoWidth != null
        ? `1.3fr ${videoWidth}px 0.7fr`
        : '1fr 2fr 0.5fr',
      alignItems          : 'stretch',
      gap                 : 6,
      padding             : 5,
      flex                : 1,
      minHeight           : 0,
      height              : '100%',
      overflow            : 'hidden',
      boxSizing           : 'border-box',
    }}>
<div style={{ overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 24, height: '100%' }}>
  <SourceSelector
    locked                      = {!channelActive}
    streamSource                = {streamSource}
    defaultSource               = {defaultStreamSource}
    onStreamSourceChange        = {handleStreamSourceChange  ?? (() => {})}
    onDefaultStreamSourceChange = {handleDefaultStreamSourceChange ?? (() => {})}
    radioGroupName              = "sourceConfig-Multiview"
    fontSize                    = '12px'
  />
  <div style={{
    background    : 'transparent',
    border        : 'none',
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'center',
    justifyContent: 'center',
    gap           : 4,
  }}>
    <span style={{ fontSize: 10, color: '#ffffff', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center' }}>
      Connexions simultanées
    </span>
    <span style={{ fontSize: 18, fontFamily: 'monospace', fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
      {simultaneousConnections}
    </span>
  </div>
</div>

      <div style={{
        background   : 'rgba(0,0,0,0.4)',
        border       : '1px solid rgba(255,255,255,0.07)',
        borderRadius : 7,
        overflow     : 'hidden',
        alignSelf    : 'start',
        ...(videoHeight != null ? { height: videoHeight } : { aspectRatio: '16 / 9' }),
      }}>
        <div style={{ width: '100%', height: '100%' }} onClick={() => setTvOpen(true)}>
          <MonitorVideoPlayer
            mediaStream = {hlsMonitorStream ?? null}
            onClick={channelActive && hlsMonitorStream?.active ? () => setTvOpen(true) : undefined}
            active      = {channelActive}
          />
        </div>
        {tvOpen && ReactDOM.createPortal(
          <Box width="fit-content" onClose={() => setTvOpen(false)} marginTop="0px" contentPadding="0px">
            <div style={{ aspectRatio: '16 / 9', height: '95vh', overflow: 'hidden', borderRadius: 8 }}>
              <ClonedVideoPlayer mediaStream={hlsMonitorStream ?? null} />
            </div>
          </Box>,
          portalTarget
        )}
      </div>

      <div style={{ minWidth: 0, display: 'flex', alignItems: 'stretch' }}>
        <HlsAudioPanel
          audioSourceRegistry = {hlsAudioRegistry}
          audioSourceId       = {hlsAudioSourceId}
          externalVolume      = {hlsVolume}
          externalMuted       = {hlsMuted}
          onVolumeChange      = {onHlsVolumeChange}
          onMutedChange       = {onHlsMutedChange}
        />
      </div>
    </div>
  );
});

// ─── IntercommScreen ─────────────────────────────────────────────────────────

/** Couleur du bord de la caméra dont le VU-mètre de l'Intercomm suit le son. */
const INTERCOMM_SELECT_COLOR = '#38bdf8';

const IntercommScreen: React.FC<{
  label      : string;
  stream?    : MediaStream;
  isSpeaking?: boolean;
  selected?  : boolean;
  onSelect?  : () => void;
}> = ({ label, stream, isSpeaking = false, selected = false, onSelect }) => {
  const videoRef    = useRef<HTMLVideoElement>(null);
  const meterRafRef = useRef<number | null>(null);
  const portalTarget = usePortalTarget();
  const [zoomOpen,   setZoomOpen]   = useState(false);

  // Le niveau ne passe plus par un state : il ne sert qu'au halo vert, écrit
  // directement sur la tuile. La boucle rAF re-rendait sinon ce composant — et
  // son <video> — à 60 fps pendant toute la prise de parole.
  const tileRef       = useRef<HTMLDivElement>(null);
  const isSpeakingRef = useRef(isSpeaking); isSpeakingRef.current = isSpeaking;
  const lastLevelRef  = useRef(0);

  const applyGlow = useCallback((level: number) => {
    const el = tileRef.current;
    if (!el) return;
    if (!isSpeakingRef.current) { el.style.boxShadow = 'none'; return; }
    const glowSize = Math.round(6 + level * 0.18);
    el.style.boxShadow = `0 0 ${glowSize}px ${Math.round(glowSize / 2)}px rgba(34,197,94,${0.4 + level * 0.004})`;
  }, []);

  // Repeint le halo quand la prise de parole s'arrête : plus aucune frame ne
  // viendra le faire.
  useEffect(() => { applyGlow(lastLevelRef.current); }, [isSpeaking, applyGlow]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (stream?.active) {
      if (el.srcObject !== stream) { el.srcObject = stream; el.play().catch(() => {}); }
    } else {
      el.srcObject = null;
    }
  }, [stream]);

  // VU-mètre pour le halo vert
  useEffect(() => {
    if (!isSpeaking || !stream?.active) {
      if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
      lastLevelRef.current = 0;
      applyGlow(0);
      return;
    }
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) return;

    const ctx      = new AudioContext();
    const src      = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.3;
    src.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);

    const loop = () => {
      analyser.getByteFrequencyData(data);
      let peak = 0;
      for (let i = 0; i < data.length; i++) if (data[i] > peak) peak = data[i];
      const pct = Math.round((peak / 255) * 100);
      lastLevelRef.current = pct;
      applyGlow(pct);
      meterRafRef.current = requestAnimationFrame(loop);
    };
    meterRafRef.current = requestAnimationFrame(loop);

    return () => {
      if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
      try { ctx.close(); } catch {}
      lastLevelRef.current = 0;
      applyGlow(0);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpeaking, stream]);

  const hasStream = !!(stream?.active);

  return (
    <>
      <div
        ref={tileRef}
        onClick={() => onSelect?.()}
        onDoubleClick={() => setZoomOpen(true)}
        title={`${label} — clic : écouter au VU-mètre, double-clic : agrandir`}
        style={{
          position    : 'relative',
          width       : '100%',
          aspectRatio : '16 / 9',
          background  : '#000',
          borderRadius: 4,
          overflow    : 'hidden',
          cursor      : 'pointer',
          // Caméra suivie par le VU-mètre : bord épais coloré, qui prime sur le liseré de prise de parole.
          border      : selected ? `4px solid ${INTERCOMM_SELECT_COLOR}`
                                 : `1.5px solid ${isSpeaking ? 'rgba(34,197,94,0.9)' : 'rgba(255,255,255,0.08)'}`,
          boxSizing   : 'border-box',
          // `boxShadow` : piloté par applyGlow, volontairement absent d'ici.
          transition  : 'border-color 0.1s',
        }}
      >
        <video ref={videoRef} autoPlay playsInline muted
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
        {!hasStream && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <VideoOff style={{ width: '35%', height: '35%', opacity: 0.12, color: '#fff' }} />
          </div>
        )}
        <div style={{
          position    : 'absolute', top: 3, left: 4,
          fontSize    : 9, fontFamily: 'monospace', fontWeight: 700,
          color       : isSpeaking ? 'rgba(34,197,94,0.9)' : '#ffffff',
          lineHeight  : 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          maxWidth    : 'calc(100% - 8px)', pointerEvents: 'none',
        }}>
          {label}
        </div>
        {isSpeaking && (
          <div style={{
            position      : 'absolute', bottom: 3, right: 3,
            fontSize      : 7, fontWeight: 800, letterSpacing: '0.4px',
            color         : '#fff', background: 'rgba(34,197,94,0.85)',
            borderRadius  : 2, padding: '1px 4px', pointerEvents: 'none',
            animation     : 'mvPulse 1s ease-in-out infinite',
          }}>
            📞 LIVE
          </div>
        )}
      </div>

      {/* ── Zoom en plein écran via Box + ClonedVideoPlayer ── */}
      {zoomOpen && ReactDOM.createPortal(
        <Box width="fit-content" onClose={() => setZoomOpen(false)} marginTop="0px" contentPadding="0px">
          <div style={{ aspectRatio: '16 / 9', height: '95vh', overflow: 'hidden', borderRadius: 8, position: 'relative' }}>
            <ClonedVideoPlayer mediaStream={stream ?? null} />
            {/* Label en overlay */}
            <div style={{
              position: 'absolute', top: 8, left: 12,
              fontSize: 13, fontFamily: 'monospace', fontWeight: 700,
              color: isSpeaking ? '#22c55e' : '#ffffff',
              textShadow: '0 1px 4px rgba(0,0,0,0.8)',
              pointerEvents: 'none',
            }}>
              {label}
            </div>
            {isSpeaking && (
              <div style={{
                position: 'absolute', bottom: 10, right: 12,
                fontSize: 11, fontWeight: 800, letterSpacing: '0.5px',
                color: '#fff', background: 'rgba(34,197,94,0.85)',
                borderRadius: 3, padding: '2px 8px',
                animation: 'mvPulse 1s ease-in-out infinite',
              }}>
                📞 LIVE
              </div>
            )}
          </div>
        </Box>,
        portalTarget
      )}
    </>
  );
};

// ─── IntercommCameraButton ────────────────────────────────────────────────────

const IntercommCameraButton: React.FC<{
  device     : RemoteDevice;
  isOn       : boolean;
  isSpeaking?: boolean;
  onToggle   : () => void;
}> = ({ device, isOn, isSpeaking = false, onToggle }) => {
  // La lueur suit le niveau audio à 60 fps : elle est écrite directement dans le
  // DOM, jamais rendue par React. `boxShadow` est donc absent du style JSX —
  // sinon chaque rendu de React viendrait écraser la valeur courante.
  const btnRef = useRef<HTMLButtonElement>(null);

  const isOnRef       = useRef(isOn);       isOnRef.current       = isOn;
  const isSpeakingRef = useRef(isSpeaking); isSpeakingRef.current = isSpeaking;
  const lastLevelRef  = useRef(0);

  const applyGlow = useCallback((level: number) => {
    const el = btnRef.current;
    if (!el) return;
    if (isSpeakingRef.current) {
      const glowSize = Math.round(6 + level * 0.18);
      el.style.boxShadow = `0 0 ${glowSize}px ${Math.round(glowSize / 2)}px rgba(34,197,94,${0.4 + level * 0.004})`;
    } else {
      el.style.boxShadow = isOnRef.current ? '0 0 6px rgba(245,158,11,0.3)' : 'none';
    }
  }, []);

  useAudioLevelEffect(device.device_id, ({ avg }) => {
    lastLevelRef.current = avg;
    applyGlow(avg);
  }, intercomLevelStore);

  // Le device peut cesser de parler ou d'être activé sans qu'aucune frame ne
  // suive : il faut alors repeindre la lueur depuis le dernier niveau connu.
  useEffect(() => { applyGlow(lastLevelRef.current); }, [isOn, isSpeaking, applyGlow]);

  return (
    <button
      ref={btnRef}
      onClick={onToggle}
      title={isOn ? `Couper l'envoi vers ${device.device_id}` : `Parler vers ${device.device_id}`}
      style={{
        background  : isOn ? 'rgba(245,158,11,0.22)' : 'rgba(255,255,255,0.05)',
        border      : `1.5px solid ${
          isSpeaking ? 'rgba(34,197,94,0.9)'
          : isOn     ? 'rgba(245,158,11,0.7)'
          : 'rgba(255,255,255,0.1)'
        }`,
        borderRadius: 6,
        cursor      : 'pointer',
        padding     : '0 8px',
        display     : 'flex',
        alignItems  : 'center',
        gap         : 6,
        width       : '100%',
        height      : 36,
        minHeight   : 36,
        maxHeight   : 36,
        transition  : 'background 0.12s, border-color 0.12s, box-shadow 0.1s',
        // `boxShadow` : piloté par applyGlow, volontairement absent d'ici.
        boxSizing   : 'border-box',
        flexShrink  : 0,
      }}
    >
      <Phone style={{
        width: 13, height: 13, flexShrink: 0,
        color: isSpeaking ? '#22c55e' : isOn ? '#f59e0b' : 'rgba(148,163,184,0.4)',
      }} />
      <span style={{
        fontSize: 11, fontFamily: 'monospace', fontWeight: 700,
        // ← toujours blanc
        color: '#ffffff',
        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', lineHeight: 1, flex: 1,
      }}>
        {device.device_id}
      </span>
      <span style={{
        fontSize: 9, fontFamily: 'monospace', fontWeight: 800,
        color: isSpeaking ? '#22c55e' : isOn ? '#f59e0b' : 'transparent',
        letterSpacing: '0.4px', flexShrink: 0,
        animation: (isSpeaking || isOn) ? 'mvPulse 1s ease-in-out infinite' : 'none',
        userSelect: 'none',
      }}>
        {isSpeaking ? '📞' : 'ON'}
      </span>
    </button>
  );
};

// ─── Helpers grille intercomm ─────────────────────────────────────────────────

function intercommCols(count: number): number {
  if (count <= 1) return 1;
  if (count <= 4) return 2;
  return 3;
}
function intercommCells(count: number): number {
  const c = intercommCols(count);
  return c * c;
}

// ─── IntercommView ────────────────────────────────────────────────────────────

interface IntercommViewProps {
  hlsVolume?         : number;
  hlsMuted?          : boolean;
  onHlsVolumeChange? : (v: number) => void;
  onHlsMutedChange?  : (m: boolean) => void;
  hlsAudioRegistry?  : AudioSourceRegistry;
  hlsAudioSourceId?  : string;
  remoteDevices?     : RemoteDevice[];
  previewStreams?     : Map<string, MediaStream>;
  localMics?         : { deviceId: string; label: string }[];
  screenWidth?       : number | null;
  screenHeight?      : number | null;
  // ── Intercomm ─────────────────────────────────────────────────────────────
  intercommMicStream?         : MediaStream | null;
  intercommDeviceStreams?     : Map<string, MediaStream>;
  intercommSpeakingDevices?   : Set<string>;
  onIntercommActivate?        : (active: boolean, selectedDevices: Set<string>) => void;
  onIntercommMicGainChange?   : (linearGain: number) => void;
  onMicChange?                : (deviceId: string) => void;
}

const IntercommView: React.FC<IntercommViewProps> = React.memo(({
  hlsVolume, hlsMuted, onHlsVolumeChange, onHlsMutedChange,
  hlsAudioRegistry, hlsAudioSourceId,
  remoteDevices   = [],
  previewStreams   = new Map(),
  localMics       = [],
  screenWidth,
  screenHeight,
  intercommMicStream       = null,
  intercommDeviceStreams   = new Map<string, MediaStream>(),
  intercommSpeakingDevices = new Set<string>(),
  onIntercommActivate,
  onIntercommMicGainChange,
  onMicChange,
}) => {
  const [micActive,       setMicActive]       = useState(false);
  const [selectedCameras, setSelectedCameras] = useState<Set<string>>(new Set());
  const [selectedMicId,   setSelectedMicId]   = useState<string>('');

  // ── VU-mètres groupés pour les devices qui parlent ────────────────────────
  // Les niveaux ne sont plus un state : la boucle rAF allouait une Map neuve par
  // frame et par device, ce qui re-rendait tout ce panneau (et annulait son
  // React.memo) tant qu'un device parlait. Elle publie maintenant dans
  // `intercomLevelStore` ; c'est IntercommCameraButton qui s'y abonne et écrit
  // sa lueur directement dans le DOM.
  const meterRafsRef = useRef<Map<string, number>>(new Map());
  const meterCtxsRef = useRef<Map<string, AudioContext>>(new Map());

  useEffect(() => {
    const speaking = intercommSpeakingDevices;
    const streams  = intercommDeviceStreams;

    // Arrêter les meters des devices qui ne parlent plus
    meterRafsRef.current.forEach((rafId, did) => {
      if (!speaking.has(did)) {
        cancelAnimationFrame(rafId);
        meterRafsRef.current.delete(did);
        const ctx = meterCtxsRef.current.get(did);
        if (ctx) { try { ctx.close(); } catch {} meterCtxsRef.current.delete(did); }
        intercomLevelStore.remove(did);
      }
    });

    // Démarrer les meters des nouveaux devices qui parlent
    speaking.forEach(did => {
      if (meterRafsRef.current.has(did)) return;
      const stream = streams.get(did);
      if (!stream?.active || !stream.getAudioTracks().length) return;

      const ctx      = new AudioContext();
      const src      = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      const loop = () => {
        analyser.getByteFrequencyData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) if (data[i] > peak) peak = data[i];
        const pct = Math.round((peak / 255) * 100);
        // Mesure mono : les trois champs portent la même valeur.
        intercomLevelStore.set(did, { avg: pct, left: pct, right: pct });
        meterRafsRef.current.set(did, requestAnimationFrame(loop));
      };
      meterRafsRef.current.set(did, requestAnimationFrame(loop));
      meterCtxsRef.current.set(did, ctx);
    });

    return () => {
      meterRafsRef.current.forEach((id, did) => {
        cancelAnimationFrame(id);
        intercomLevelStore.remove(did);
      });
      meterCtxsRef.current.forEach(ctx => { try { ctx.close(); } catch {} });
    };
  }, [intercommSpeakingDevices, intercommDeviceStreams]);

  // Signaler au parent le micro sélectionné (il gère getUserMedia + replaceTrack)
  // On skip le premier rendu pour ne pas écraser le micro par défaut déjà capturé.
  const isFirstMicRender = useRef(true);
  useEffect(() => {
    if (isFirstMicRender.current) { isFirstMicRender.current = false; return; }
    onMicChange?.(selectedMicId);
  }, [selectedMicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleCamera = useCallback((id: string) => {
    setSelectedCameras(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      // Si l'intercomm est déjà actif, réévaluer immédiatement quels devices reçoivent le son
      if (micActive) onIntercommActivate?.(true, next);
      return next;
    });
  }, [micActive, onIntercommActivate]);

  // Toggle mic intercomm régie → devices sélectionnés
  const handleMicClick = useCallback(() => {
    const next = !micActive;
    setMicActive(next);
    onIntercommActivate?.(next, selectedCameras);
  }, [micActive, selectedCameras, onIntercommActivate]);

  // Max 6 caméras connectées
  const connectedDevices = remoteDevices.filter(d => d.is_connected).slice(0, 6);

  // Caméra dont le 2e VU-mètre suit le son : celle qu'on a cliquée, sinon la première branchée.
  // Aucune caméra externe branchée : pas de flux, le VU-mètre reste inactif.
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(null);
  const meteredDeviceId = connectedDevices.find(d => d.device_id === selectedScreenId)?.device_id
                       ?? connectedDevices[0]?.device_id ?? null;
  const meteredStream   = meteredDeviceId ? (previewStreams.get('remote-' + meteredDeviceId) ?? null) : null;

  // Touche « Parler » du Stream Deck : même bascule que PARLER / ARRÊTER, vers
  // les caméras cochées ici (aucune cochée : le micro s'ouvre sans destinataire,
  // comme à la souris — la touche le dit).
  const talkTargets = Array.from(selectedCameras).filter(id => connectedDevices.some(d => d.device_id === id)).length;
  useStreamDeckControl('talk', {
    available: true,
    active:    micActive,
    busy:      false,
    detail:    talkTargets === 0 ? 'aucune caméra' : `${talkTargets} caméra${talkTargets > 1 ? 's' : ''}`,
  }, cmd => {
    const next = cmd.action === 'toggle' ? !micActive : cmd.action === 'start';
    if (next !== micActive) handleMicClick();
  });
  const cols    = intercommCols(connectedDevices.length);
  const cells   = intercommCells(connectedDevices.length);
  const colsStr = `repeat(${cols}, 1fr)`;

  const screenW = screenWidth  ?? null;
  // On contraint seulement la largeur quand on la connaît ; la hauteur suit toujours
  // le flex parent (height: '100%') pour éviter l'espace résiduel en grille 2 lignes.
  const screensStyle: React.CSSProperties = screenW != null
    ? { flexShrink: 0, width: screenW, height: '100%' }
    : { flexShrink: 1, minWidth: 0, height: '100%' };

  return (
    <div style={{
      display: 'flex', flexDirection: 'row', alignItems: 'stretch',
      gap: 6, padding: 5, flex: 1, minHeight: 0, height: '100%',
      overflow: 'hidden', boxSizing: 'border-box',
    }}>

      {/* Colonne gauche : boutons + select mic + bouton téléphone */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' }}>

        {/* Grille de boutons — hauteur fixe 36 px par bouton, pas height:100% */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {connectedDevices.length === 0 ? (
            <div style={{
              height: '100%', background: 'rgba(0,0,0,0.2)',
              border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.35,
            }}>
              <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(148,163,184,0.7)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center' }}>
                Aucune caméra
              </span>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: colsStr,
              gridAutoRows: '1fr',
              gap: 4,
              alignContent: 'start',
            }}>
              {Array.from({ length: cells }, (_, i) => {
                const dev = connectedDevices[i];
                if (!dev) return <div key={i} style={{ background: 'transparent' }} />;
                return (
                  <IntercommCameraButton
                    key={dev.device_id}
                    device={dev}
                    isOn={selectedCameras.has(dev.device_id)}
                    isSpeaking={intercommSpeakingDevices.has(dev.device_id)}
                    onToggle={() => handleToggleCamera(dev.device_id)}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Select micro local — style SourceConfigModal */}
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Micro régie
          </span>
          <select
            value={selectedMicId}
            onChange={e => setSelectedMicId(e.target.value)}
            style={{
              width        : '100%',
              padding      : '8px 12px',
              borderRadius : 8,
              background   : '#0f172a',
              border       : '1px solid #334155',
              color        : '#e2e8f0',
              fontSize     : 13,
              outline      : 'none',
              cursor       : 'pointer',
              appearance   : 'auto',
              colorScheme  : 'dark',
              WebkitTextFillColor: '#e2e8f0',
            }}
          >
            <option value="" style={{ background: '#1a2035', color: '#e2e8f0' }}>— Défaut —</option>
            {localMics.map(m => (
              <option key={m.deviceId} value={m.deviceId} style={{ background: '#1a2035', color: '#e2e8f0' }}>
                {m.label || `Micro ${m.deviceId.slice(0, 8)}`}
              </option>
            ))}
          </select>
        </div>

        {/* Bouton téléphone toggle */}
        <button
          onClick={handleMicClick}
          title={micActive ? 'Raccrocher l\'intercomm' : 'Activer l\'intercomm'}
          style={{
            flexShrink: 0, minHeight: 36, maxHeight: 36, height: 36, width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background  : micActive ? 'rgba(239,68,68,0.18)' : 'rgba(255,255,255,0.04)',
            border      : `1.5px solid ${micActive ? 'rgba(239,68,68,0.7)' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 7, cursor: 'pointer',
            transition  : 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
            boxShadow   : micActive ? '0 0 10px rgba(239,68,68,0.35)' : 'none',
          }}
        >
          {micActive
            ? <Phone    style={{ width: 13, height: 13, color: '#ef4444', flexShrink: 0 }} />
            : <PhoneOff style={{ width: 13, height: 13, color: 'rgba(148,163,184,0.4)', flexShrink: 0 }} />
          }
          <span style={{
            fontSize: 11, fontFamily: 'monospace', fontWeight: 800, letterSpacing: '0.4px',
            // ← toujours blanc quand inactif, rouge quand actif
            color: micActive ? '#ef4444' : '#ffffff',
            animation: micActive ? 'mvPulse 1s ease-in-out infinite' : 'none',
          }}>
            {/* Deux VERBES qui décrivent l'action du clic, et non l'état courant :
                « MICRO FERME » se lisait comme un constat — micro fermé — alors
                que le bouton était justement là pour le fermer. */}
            {micActive ? 'ARRÊTER' : 'PARLER'}
          </span>
        </button>
      </div>

      {/* Panneau micro régie — VU-mètre + gain vers smartphone */}
      <div style={{ flexShrink: 0, display: 'flex', alignSelf: 'stretch' }}>
        <HlsAudioPanel
          mediaStream  = {intercommMicStream}
          noOutput     = {true}
          onGainChange = {onIntercommMicGainChange}
        />
      </div>

      {/* Bloc écrans : dimensions figées = vidéo de retour */}
      <div style={{ ...screensStyle, overflow: 'hidden' }}>
        {connectedDevices.length === 0 ? (
          <div style={{
            width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, opacity: 0.2 }}>
              <VideoOff style={{ width: 24, height: 24, color: '#fff' }} />
              <span style={{ fontSize: 9, fontFamily: 'monospace', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Aucune caméra
              </span>
            </div>
          </div>
        ) : (
          <div style={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns : colsStr,
            gridAutoRows        : 'auto',
            gap: 3,
          }}>
            {Array.from({ length: cells }, (_, i) => {
              const dev = connectedDevices[i];
              if (!dev) return <div key={i} style={{ background: 'transparent' }} />;
              return (
                <IntercommScreen
                  key={dev.device_id}
                  label={dev.device_id}
                  stream={previewStreams.get('remote-' + dev.device_id)}
                  isSpeaking={intercommSpeakingDevices.has(dev.device_id)}
                  selected={dev.device_id === meteredDeviceId}
                  onSelect={() => setSelectedScreenId(dev.device_id)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* VU-mètre de la caméra sélectionnée (bord épais sur sa mini-vue). Il n'est PLUS celui du retour
          diffusion, qui reste dans l'onglet « Diffusion du flux final ». `key` : un autre flux = un autre
          graphe audio. Avec sortie : on ÉCOUTE la caméra choisie (curseur = volume d'écoute, coupure
          comprise) ; ce chemin est local à la régie et ne rejoint ni le mixeur ni le bus WHIP. */}
      <div style={{ flexShrink: 0, display: 'flex', alignSelf: 'stretch' }}>
        <HlsAudioPanel
          key         = {meteredDeviceId ?? 'aucune'}
          mediaStream = {meteredStream}
        />
      </div>
    </div>
  );
});

// ─── CarteView ────────────────────────────────────────────────────────────────
//
// Où sont les caméras distantes, et vers quoi elles regardent.
//
// Trois zones : la liste des appareils à gauche, la carte au milieu, la boussole
// et l'inclinomètre à droite. La sélection est unique et partagée par les trois —
// cliquer un point allume la ligne, cliquer la ligne allume le point.
//
// La position et la visée viennent du device lui-même (message `device_geo`) : ni
// le flux vidéo ni le serveur ne les portent. Un appareil qui n'a pas encore
// autorisé la localisation est listé, mais sans point sur la carte — le dire est
// plus utile que de le faire disparaître.

const CARDINALS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
                   'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'] as const;

const cardinalPoint = (heading: number): string =>
  CARDINALS[Math.round((((heading % 360) + 360) % 360) / 22.5) % 16];

/** Le fond de carte du tableau de bord, repris tel quel. */
const CARTE_MAP_COLORS = {
  water:      '#1b2947',
  land:       '#263d6e',
  landcover:  '#263d6e',
  road:       '#2a2a4a',
  building:   '#222244',
  labelText:  '#c7d2fe',
  labelHalo:  '#0b1220',
};

// ── Boussole ─────────────────────────────────────────────────────────────────
const Compass: React.FC<{ heading: number | null; source: 'compass' | 'absolute' | null }> = ({ heading, source }) => {
  const R = 46;
  const known = heading != null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <svg viewBox="0 0 110 110" width={110} height={110}>
        <circle cx="55" cy="55" r={R} fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        {[0, 90, 180, 270].map(a => (
          <text key={a} x="55" y="16"
                transform={`rotate(${a} 55 55)`}
                textAnchor="middle" fontSize="10" fontFamily="monospace"
                fill={a === 0 ? '#f87171' : 'rgba(148,163,184,0.7)'}>
            {a === 0 ? 'N' : a === 90 ? 'E' : a === 180 ? 'S' : 'O'}
          </text>
        ))}
        {Array.from({ length: 24 }, (_, i) => i * 15).map(a => (
          <line key={a} x1="55" y1={55 - R + 3} x2="55" y2={55 - R + (a % 90 === 0 ? 10 : 6)}
                transform={`rotate(${a} 55 55)`}
                stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
        ))}
        {known && (
          <g transform={`rotate(${heading} 55 55)`}>
            {/* le cône dit l'ouverture du champ, l'aiguille la direction exacte */}
            <path d="M55 55 L38 18 A40 40 0 0 1 72 18 Z" fill="#22d3ee" opacity="0.18" />
            <line x1="55" y1="55" x2="55" y2="19" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" />
          </g>
        )}
        <circle cx="55" cy="55" r="3" fill={known ? '#22d3ee' : 'rgba(148,163,184,0.5)'} />
      </svg>
      <div style={{ fontSize: 11, fontFamily: 'monospace', color: known ? '#22d3ee' : 'rgba(148,163,184,0.5)' }}>
        {known ? `${heading!.toFixed(0)}° ${cardinalPoint(heading!)}` : 'cap inconnu'}
      </div>
      {/* L'origine du cap n'est pas un détail : sans référence au nord, un chiffre
          d'orientation n'est qu'une rotation relative au démarrage de la page. */}
      <div style={{ fontSize: 8, fontFamily: 'monospace', color: 'rgba(148,163,184,0.4)' }}>
        {source === 'compass' ? 'boussole magnétique'
          : source === 'absolute' ? 'orientation absolue'
          : 'aucune référence au nord'}
      </div>
    </div>
  );
};

// ── Inclinomètre ─────────────────────────────────────────────────────────────
const Inclinometer: React.FC<{ elevation: number | null }> = ({ elevation }) => {
  const known = elevation != null;
  const clamped = Math.max(-90, Math.min(90, elevation ?? 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <svg viewBox="0 0 110 78" width={110} height={78}>
        <rect x="6" y="6" width="98" height="66" rx="6" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.15)" />
        {/* la ligne d'horizon est la référence : elle ne bouge jamais */}
        <line x1="14" y1="39" x2="96" y2="39" stroke="rgba(148,163,184,0.45)" strokeWidth="1" strokeDasharray="3 3" />
        <text x="99" y="37" textAnchor="end" fontSize="7" fontFamily="monospace" fill="rgba(148,163,184,0.45)">horizon</text>
        {known && (
          <g transform={`rotate(${-clamped} 30 39)`}>
            <line x1="30" y1="39" x2="92" y2="39" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" />
            <polygon points="92,34 100,39 92,44" fill="#22d3ee" />
          </g>
        )}
        <circle cx="30" cy="39" r="3.5" fill={known ? '#22d3ee' : 'rgba(148,163,184,0.5)'} />
      </svg>
      <div style={{ fontSize: 11, fontFamily: 'monospace', color: known ? '#22d3ee' : 'rgba(148,163,184,0.5)' }}>
        {known ? `${clamped > 0 ? '+' : ''}${clamped.toFixed(0)}°` : 'inclinaison inconnue'}
      </div>
      <div style={{ fontSize: 8, fontFamily: 'monospace', color: 'rgba(148,163,184,0.4)' }}>
        {!known ? '—' : clamped > 3 ? 'vise le ciel' : clamped < -3 ? 'vise le sol' : 'à l’horizontale'}
      </div>
    </div>
  );
};

// ── Dévers ───────────────────────────────────────────────────────────────────
//
// La seconde inclinaison, celle que l'horizon ne dit pas. L'inclinomètre répond à
// « vise-t-on haut ou bas ? » ; celui-ci répond à « le cadre est-il droit ? ».
// Le rectangle est le cadre de l'image et il tourne ; la ligne d'horizon, elle,
// ne bouge jamais — c'est la référence.
const RollIndicator: React.FC<{ roll: number | null; screenMode?: 'portrait' | 'landscape' | null }> = ({ roll, screenMode }) => {
  const known = roll != null;
  const r     = roll ?? 0;
  // Le rectangle prend la forme que l'appareil déclare : voir un cadre vertical
  // dit d'un coup d'œil qu'on filme en portrait, sans lire la ligne de texte.
  const w = screenMode === 'portrait' ? 26 : 44;
  const h = screenMode === 'portrait' ? 44 : 26;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <svg viewBox="0 0 110 78" width={110} height={78}>
        <rect x="6" y="6" width="98" height="66" rx="6" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.15)" />
        <line x1="14" y1="39" x2="96" y2="39" stroke="rgba(148,163,184,0.45)" strokeWidth="1" strokeDasharray="3 3" />
        {known && (
          // Rotation horaire pour un dévers positif : le haut de l'appareil
          // penche à droite, le cadre suit.
          <g transform={`rotate(${r} 55 39)`}>
            <rect x={55 - w / 2} y={39 - h / 2} width={w} height={h} rx="3"
                  fill="none" stroke="#22d3ee" strokeWidth="2" />
            {/* le trait épais marque le HAUT du cadre : sans lui, un dévers de
                180° ressemblerait à un cadre droit */}
            <line x1={55 - w / 2} y1={39 - h / 2} x2={55 + w / 2} y2={39 - h / 2}
                  stroke="#22d3ee" strokeWidth="4" strokeLinecap="round" />
          </g>
        )}
        {!known && (
          <rect x={55 - 22} y={39 - 13} width="44" height="26" rx="3"
                fill="none" stroke="rgba(148,163,184,0.35)" strokeWidth="2" strokeDasharray="3 3" />
        )}
      </svg>
      <div style={{ fontSize: 11, fontFamily: 'monospace', color: known ? '#22d3ee' : 'rgba(148,163,184,0.5)' }}>
        {known ? `${r > 0 ? '+' : ''}${r.toFixed(0)}°` : 'dévers inconnu'}
      </div>
      <div style={{ fontSize: 8, fontFamily: 'monospace', color: 'rgba(148,163,184,0.4)' }}>
        {!known ? '—' : Math.abs(r) < 3 ? 'cadre droit' : r > 0 ? 'penché à droite' : 'penché à gauche'}
      </div>
    </div>
  );
};

interface CarteViewProps {
  remoteDevices?: RemoteDevice[];
  deviceGeo?    : Map<string, DeviceGeoReport>;
  onGeoWatch?   : (active: boolean, deviceIds: string[]) => void;
}

const CarteView: React.FC<CarteViewProps> = React.memo(({ remoteDevices = [], deviceGeo, onGeoWatch }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Seuls les appareils connectés ont leur place ici : la carte sert à savoir où
  // se trouve quelqu'un maintenant. Un device hors ligne n'a de toute façon plus
  // de relevé — le microservice ne conserve rien.
  const devices = useMemo(
    () => remoteDevices.filter(d => d.is_connected),
    [remoteDevices],
  );

  const markers = useMemo<MapMarker[]>(() => devices.flatMap(d => {
    const geo = deviceGeo?.get(d.device_id);
    if (!geo) return [];
    return [{
      id      : d.device_id,
      lat     : geo.lat,
      lng     : geo.lng,
      label   : d.label || d.device_id,
      heading : geo.heading ?? null,
      color   : d.device_id === selectedId ? '#f59e0b' : '#22d3ee',
      selected: d.device_id === selectedId,
    }];
  }), [devices, deviceGeo, selectedId]);

  // La sélection ne survit pas à la disparition de son appareil : garder l'ancien
  // identifiant afficherait une boussole orpheline, figée sur un dernier relevé.
  useEffect(() => {
    if (selectedId && !devices.some(d => d.device_id === selectedId)) setSelectedId(null);
  }, [devices, selectedId]);

  // ── Abonnement : un appareil n'émet sa position que si on la lui demande ────
  //
  // Sans cet abonnement, il fallait compter sur le porteur pour ouvrir « Infos »
  // au moins une fois — et la carte restait vide sans que rien ne le dise. On
  // s'abonne donc à l'ouverture de cet onglet (ce composant n'est monté que tant
  // qu'il est affiché) et on se désabonne à sa fermeture : le suivi coûte la puce
  // GPS allumée sur un téléphone qui encode et téléverse déjà.
  //
  // Un appareil qui se connecte pendant que la carte est ouverte est abonné à son
  // tour ; celui qui s'en va est simplement oublié — il a perdu son suivi avec sa
  // session, et se réabonnera en revenant.
  //
  // ⚠️ Ne donne pas le cap d'un iPhone : l'autorisation d'orientation y exige un
  // geste du porteur, qu'aucun message ne remplace. Position oui, boussole et
  // inclinaison seulement après un appui sur « Infos ».
  const armedRef    = useRef<Set<string>>(new Set());
  const geoWatchRef = useRef(onGeoWatch);
  useEffect(() => { geoWatchRef.current = onGeoWatch; }, [onGeoWatch]);

  // La liste d'identifiants, et non le tableau d'appareils : celui-ci est
  // reconstruit à chaque rafraîchissement de la liste (niveau de batterie, débit…)
  // et relancerait l'effet pour rien.
  const connectedIds = useMemo(() => devices.map(d => d.device_id), [devices]);
  const connectedKey = connectedIds.join('|');

  useEffect(() => {
    const armed   = armedRef.current;
    const present = new Set(connectedIds);
    armed.forEach(id => { if (!present.has(id)) armed.delete(id); });
    const toArm = connectedIds.filter(id => !armed.has(id));
    if (toArm.length === 0) return;
    toArm.forEach(id => armed.add(id));
    geoWatchRef.current?.(true, toArm);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedKey]);

  useEffect(() => () => {
    const armed = armedRef.current;
    // Array.from plutôt que le spread : la cible de compilation du projet ne
    // déroule pas les Set.
    if (armed.size > 0) geoWatchRef.current?.(false, Array.from(armed));
    armed.clear();
  }, []);

  const selectedGeo = selectedId ? deviceGeo?.get(selectedId) : undefined;
  const selectedDev = devices.find(d => d.device_id === selectedId);

  const rowStyle = (active: boolean, located: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '6px 8px', borderRadius: 5, cursor: 'pointer', textAlign: 'left',
    background: active ? 'rgba(245,158,11,0.18)' : 'rgba(255,255,255,0.04)',
    border    : `1px solid ${active ? 'rgba(245,158,11,0.55)' : 'rgba(255,255,255,0.08)'}`,
    color     : active ? '#fcd34d' : located ? '#cbd5e1' : 'rgba(148,163,184,0.55)',
    fontSize: 11, fontFamily: 'monospace', width: '100%',
    transition: 'background 0.15s, border-color 0.15s',
  });

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: 5, padding: 5 }}>

      {/* ── Liste des appareils ── */}
      <div style={{ width: 190, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' }}>
        <div style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(148,163,184,0.5)', padding: '2px 2px 4px' }}>
          APPAREILS CONNECTÉS ({devices.length})
        </div>
        {devices.length === 0 && (
          <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.45)', padding: 8, lineHeight: 1.5 }}>
            Aucun appareil distant connecté.
          </div>
        )}
        {devices.map(d => {
          const geo    = deviceGeo?.get(d.device_id);
          const active = d.device_id === selectedId;
          return (
            <button key={d.device_id}
                    style={rowStyle(active, !!geo)}
                    onClick={() => setSelectedId(active ? null : d.device_id)}
                    title={geo ? 'Voir sur la carte' : 'Position non communiquée par cet appareil'}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                background: geo ? (active ? '#f59e0b' : '#22d3ee') : 'rgba(148,163,184,0.35)',
                boxShadow: geo ? `0 0 6px ${active ? '#f59e0b' : '#22d3ee'}` : 'none',
              }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {d.label || d.device_id}
              </span>
              {!geo && <span style={{ fontSize: 8, opacity: 0.7 }}>sans position</span>}
            </button>
          );
        })}
      </div>

      {/* ── Carte ── */}
      <div style={{ flex: 1, minWidth: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}>
        <React.Suspense fallback={<div style={{ width: '100%', height: '100%', background: '#0b1220' }} />}>
        <OpenStreetMapV2
          coordinates={[]}
          markers={markers}
          onMarkerClick={id => setSelectedId(prev => (prev === id ? null : id))}
          // Pas de recentrage automatique : la vue sauterait à chaque relevé GPS,
          // et l'opérateur perdrait le cadrage qu'il vient de régler à la main.
          autoCenter={false}
          initialZoom={12}
          renderMode="dark"
          mapColors={CARTE_MAP_COLORS}
          cityLabelsOnly
        />
        </React.Suspense>
        {markers.length === 0 && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none', fontSize: 11, fontFamily: 'monospace',
            color: 'rgba(148,163,184,0.6)', textAlign: 'center', padding: 20, lineHeight: 1.6,
          }}>
            Aucune position reçue.<br />
            L'appareil doit autoriser la localisation depuis son écran « Infos ».
          </div>
        )}
      </div>

      {/* ── Boussole et inclinaison ── */}
      <div style={{
        width: 132, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10,
        padding: '8px 6px', borderRadius: 6,
        border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)',
        overflowY: 'auto',
      }}>
        <div style={{
          fontSize: 9, fontFamily: 'monospace', color: 'rgba(148,163,184,0.55)',
          textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {selectedDev ? (selectedDev.label || selectedDev.device_id) : 'AUCUNE SÉLECTION'}
        </div>
        <Compass heading={selectedGeo?.heading ?? null} source={selectedGeo?.headingSource ?? null} />
        <Inclinometer elevation={selectedGeo?.elevation ?? null} />
        <RollIndicator roll={selectedGeo?.roll ?? null} screenMode={selectedGeo?.screenMode ?? null} />
        {selectedGeo && (
          <div style={{ fontSize: 8, fontFamily: 'monospace', color: 'rgba(148,163,184,0.45)', textAlign: 'center', lineHeight: 1.6 }}>
            {/* Tenue et objectif ne sont pas de la décoration : le cap porte sur
                l'axe de LA caméra qui filme, et les deux visent dos à dos. */}
            <span style={{ color: 'rgba(148,163,184,0.75)' }}>
              {selectedGeo.screenMode === 'landscape' ? 'Paysage'
                : selectedGeo.screenMode === 'portrait' ? 'Portrait'
                : 'tenue inconnue'}
              {' · '}
              {selectedGeo.facing === 'rear' ? 'caméra arrière'
                : selectedGeo.facing === 'front' ? 'caméra avant'
                : 'caméra indéterminée'}
            </span>
            <br />
            {selectedGeo.lat.toFixed(5)}°<br />{selectedGeo.lng.toFixed(5)}°
            {selectedGeo.accuracy != null && <><br />±{selectedGeo.accuracy.toFixed(0)} m</>}
          </div>
        )}
      </div>
    </div>
  );
});

// ─── DebitsView ───────────────────────────────────────────────────────────────
//
// Ce que la régie fait réellement passer, tuyau par tuyau.
//
// La mesure tourne en permanence (api_bandwidth, démarré par LiveStreamManager) et
// s'écrit dans les journaux toutes les 30 s : cette vue ne fait que montrer le
// dernier relevé. Elle s'abonne DIRECTEMENT au module plutôt que de recevoir un
// état par les props — un rendu toutes les 3 secondes propagé depuis le haut
// ferait travailler tout le pupitre pour une colonne de chiffres.

const DebitsView: React.FC<{
  microserviceUrl?: string;
  remoteDevices?  : RemoteDevice[];
  onMetricsLog?   : (active: boolean, deviceIds: string[]) => void;
  programLinkActive?  : boolean;
  programLinkOnAir?   : boolean;
  onCloseProgramLink? : () => void;
  onReopenProgramLink?: () => void;
  /** Bloc de hauteur imposée (onglet détaché) : tout défile, bandeau des totaux compris. */
  scrollAll?          : boolean;
}> = React.memo(({ microserviceUrl, remoteDevices = [], onMetricsLog,
                   programLinkActive, programLinkOnAir, onCloseProgramLink, onReopenProgramLink, scrollAll = false }) => {
  const [reading, setReading] = useState<BandwidthReading | null>(getLastBandwidthReading());
  useEffect(() => subscribeBandwidth(setReading), []);

  const pipes = useMemo(() => {
    if (!reading) return [];
    // Les tuyaux qui portent quelque chose d'abord, les dormants ensuite : c'est
    // l'ordre dans lequel on les cherche quand le lien sature.
    return [...reading.pipes].sort((a, b) => (b.inBps + b.outBps) - (a.inBps + a.outBps));
  }, [reading]);

  // Le pendant local de « Reçu par MediaMTX » (qui, lui, vient du microservice) :
  // ce que LE NAVIGATEUR a mesuré avoir émis vers MediaMTX, tuyau par tuyau
  // (whip:programme, chaque angle enregistré). Les retours P2P et l'intercom
  // sont exclus : ils ne passent pas par MediaMTX, les comparer au « reçu » du
  // serveur n'aurait aucun sens.
  const envoyesMediaMtx = useMemo(
    () => pipes.filter(p => p.trajet === 'mediamtx' && (p.kind === 'whip' || p.kind === 'angle')),
    [pipes]
  );

  // Mesure à la demande du lien de la machine. Jamais automatique : elle dépense
  // 15 Mo, et lancée pendant une émission elle les prend à l'antenne. D'où le
  // bouton en deux temps — le second clic confirme en connaissance de cause.
  const [mesure, setMesure]   = useState<{ downBps: number; upBps: number; upBpsServeur?: number | null } | null>(null);
  const [mesureEnCours, setMesureEnCours] = useState(false);
  const [arme, setArme]       = useState(false);
  const [erreurMesure, setErreurMesure]   = useState<string | null>(null);
  const estimation = useMemo(() => readInternetEstimate(), [mesure]);

  const [cible, setCible] = useState<'internet' | 'cast'>('cast');

  const lancerMesure = async () => {
    if (!arme) { setArme(true); return; }
    setArme(false); setMesureEnCours(true); setErreurMesure(null);
    try {
      // Vers cast par défaut : c'est le trajet que le média emprunte. Internet
      // en second, pour savoir si une lenteur vient de la ligne ou du chemin.
      if (cible === 'cast' && microserviceUrl) {
        const r = await mesurerDebitVersCast(microserviceUrl);
        setMesure({ downBps: r.downBps, upBps: r.upBps, upBpsServeur: r.upBpsServeur });
      } else {
        setMesure(await mesurerDebitInternet());
      }
    } catch (e: any) {
      setErreurMesure(e?.message ?? 'mesure impossible');
    } finally {
      setMesureEnCours(false);
    }
  };

  // Diagnostic ICE : quels chemins ont été tentés, et ce qu'ils ont répondu.
  const [pairesIce, setPairesIce] = useState<IcePairReport[] | null>(null);

  // Ce que MediaMTX REÇOIT vraiment. Interrogé seulement quand cet onglet est
  // ouvert — c'est un appel réseau, il n'a pas à tourner en permanence — et le
  // microservice met sa réponse en cache une seconde pour N régies.
  const [recu, setRecu] = useState<any | null>(null);
  useEffect(() => {
    if (!microserviceUrl) return;
    let vivant = true;
    const lire = () => {
      fetch(`${microserviceUrl}/link-quality`, { cache: 'no-store' })
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (vivant) setRecu(d); })
        .catch(() => { if (vivant) setRecu(null); });
    };
    lire();
    const id = setInterval(lire, 3000);
    return () => { vivant = false; clearInterval(id); };
  }, [microserviceUrl]);

  const cell: React.CSSProperties = { padding: '3px 8px', fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' };
  const head: React.CSSProperties = { ...cell, fontSize: 9, letterSpacing: '0.5px', color: 'rgba(148,163,184,0.75)', textTransform: 'uppercase', textAlign: 'left', position: 'sticky', top: 0, background: 'rgba(15,23,42,0.97)' };

  const hlsIn    = pipes.filter(p => p.family === 'hls').reduce((sum, p) => sum + p.inBps, 0);
  const webrtcIn = (reading?.totalIn ?? 0) - hlsIn;

  // ── Prise de mesures ────────────────────────────────────────────────────────
  //
  // Un seul bouton, TROIS étages, parce qu'une prise molle peut venir de
  // n'importe lequel et qu'on ne sait pas lequel avant d'avoir les trois :
  //   • le poste de régie   → fichier metrics_regie_<chaîne>.txt
  //   • chaque téléphone     → fichier metrics_device_<appareil>.txt
  //   • les process ffmpeg   → process_metrics.csv, côté serveur
  //
  // Éteint par défaut, et volontairement : allumé en permanence, il écrirait des
  // milliers de lignes que personne ne relira. On l'allume le temps d'une prise.
  //
  // Chaque étage est indépendant : si le microservice refuse (route absente de la
  // liste blanche HAProxy, chaîne éteinte), la régie et les téléphones mesurent
  // quand même. Le refus est affiché, il n'annule rien.
  const [mesuresEnCours, setMesuresEnCours] = useState(isMetricsEnabled());
  const [erreurServeur, setErreurServeur]   = useState<string | null>(null);

  const basculerMesures = async () => {
    const actif = !mesuresEnCours;
    setMesuresEnCours(actif);
    setErreurServeur(null);

    setMetricsEnabled(actif);
    onMetricsLog?.(actif, remoteDevices.filter(d => d.is_connected).map(d => d.device_id));

    if (!microserviceUrl) return;
    try {
      const r = await fetch(`${microserviceUrl}/metrics/${actif ? 'start' : 'stop'}`, { method: 'POST' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (e: any) {
      setErreurServeur(e?.message ?? 'injoignable');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
                  ...(scrollAll ? { overflowY: 'auto', overflowX: 'hidden' } : { overflow: 'hidden' }) }}>
      {/* Bandeau des totaux : la seule chose à lire quand on cherche une saturation.
          Une ligne par mesure — l'explication vit en infobulle (`title`), pas dans
          le texte : en colonnes, plus de trois valeurs côte à côte devenait illisible
          (le `gap` flex qui les séparait est purement visuel, absent du texte — au
          copier-coller ou à la lecture d'écran, tout se recolle). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div>
          <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: '#38bdf8' }}
                title={`Débit descendant total : tout ce que cette régie reçoit, WebRTC et HLS confondus. Détail : webrtc ${formatBps(webrtcIn)}, hls ${formatBps(hlsIn)}.`}>
            ↓ descendant {formatBps(reading?.totalIn ?? 0)}
          </span>
        </div>
        <div>
          <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: '#f472b6' }}
                title="Débit montant total : tout ce que cette régie envoie, tous tuyaux confondus (programme, angles, retours P2P vers les téléphones, intercom). Le détail tuyau par tuyau — dont ce que CETTE chaîne envoie précisément à MediaMTX — est dans la section « Envoyé à MediaMTX », plus bas.">
            ↑ montant {formatBps(reading?.totalOut ?? 0)}
          </span>
        </div>
        {/* Le CPU des codecs et celui du fil principal sont DEUX choses : la
            première encode et décode, la seconde compose le canvas et rend
            l'interface. Une régie peut saccader avec des encodeurs au repos. */}
        <div>
          <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(226,232,240,0.9)' }}
                title="Millisecondes de CPU par seconde passées à encoder et décoder, tous tuyaux confondus. 1000 ms/s = un cœur entier.">
            cpu codecs <b style={{ color: (reading?.coeurs && reading.cpuMediaMsParSec > reading.coeurs * 700) ? '#f87171' : '#e2e8f0' }}>
              {Math.round(reading?.cpuMediaMsParSec ?? 0)} ms/s
            </b>
            {reading?.coeurs ? <span style={{ fontSize: 10, opacity: 0.7 }}> / {reading.coeurs} cœurs</span> : null}
          </span>
        </div>
        <div>
          <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(226,232,240,0.9)' }}
                title="Retard des trames d'animation : c'est le fil qui compose le canvas et rend l'interface. Saturé, la régie saccade même si les encodeurs suivent.">
            fil principal <b style={{ color: (reading?.filPrincipalCharge ?? 0) > 0.7 ? '#f87171' : '#e2e8f0' }}>
              {Math.round((reading?.filPrincipalCharge ?? 0) * 100)} %
            </b>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 3 }}>
          {/* Prise de mesures : régie + téléphones + process ffmpeg, d'un seul geste. */}
          <button onClick={basculerMesures}
            title={mesuresEnCours
              ? 'Arrêter la prise de mesures (régie, téléphones connectés, process ffmpeg)'
              : "Écrire les relevés dans des fichiers dédiés : un pour cette régie, un par téléphone connecté, un pour les process ffmpeg du serveur. À allumer le temps d'une prise, pas en permanence."}
            style={{
              fontFamily: 'monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.4px',
              padding: '3px 9px', borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap',
              border: `1px solid ${mesuresEnCours ? 'rgba(248,113,113,0.55)' : 'rgba(148,163,184,0.35)'}`,
              background: mesuresEnCours ? 'rgba(248,113,113,0.16)' : 'rgba(148,163,184,0.10)',
              color: mesuresEnCours ? '#fca5a5' : 'rgba(203,213,225,0.9)',
            }}>
            {mesuresEnCours ? '● ENREGISTRE LES MESURES' : '○ MESURES'}
          </button>
          {erreurServeur && (
            <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#fbbf24' }}
                  title="La régie et les téléphones mesurent quand même — seul l'étage serveur manque.">
              serveur : {erreurServeur}
            </span>
          )}
          <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(148,163,184,0.6)' }} title="Une source déclarée mais non tirée — une Playlist qui n'est ni en preview ni à l'antenne — ne consomme rien et n'apparaît donc pas ici.">
            {pipes.length} tuyau{pipes.length > 1 ? 'x' : ''} réellement ouvert{pipes.length > 1 ? 's' : ''} · relevé toutes les 3 s, journalisé toutes les 30 s
          </span>
        </div>
      </div>

      {/* ── Les deux liens, au-dessus des tuyaux ────────────────────────────
          Le premier est mesuré sans rien dépenser, sur les connexions WebRTC déjà
          ouvertes : c'est le seul qui décrive le trajet réellement emprunté vers
          le serveur. Le second décrit la machine, pas le trajet. */}
      <div style={{ display: 'flex', gap: 10, padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', border: '1px solid rgba(56,189,248,0.25)', borderRadius: 6, padding: '6px 10px', background: 'rgba(56,189,248,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 9, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'rgba(148,163,184,0.85)' }}>
              Vers {WWW_URLS.broadcastingServer.replace(/^https?:\/\//, '')}
            </span>
            <span style={{ flex: 1 }} />
            {/* Fermée, la connexion ne repartait que par un passage à l'antenne — et le
                bouton ayant disparu avec elle, rien à l'écran ne disait comment. D'où la
                commande inverse, au même endroit. */}
            {!programLinkActive && (
              <button
                onClick={() => onReopenProgramLink?.()}
                title="Rouvre la publication permanente du programme vers le serveur. Sans elle, les téléphones n'ont pas de retour, et le passage à l'antenne doit rétablir la connexion avant d'émettre."
                style={{
                  background: 'rgba(74,222,128,0.15)',
                  border: '1px solid rgba(74,222,128,0.45)',
                  color: '#86efac',
                  borderRadius: 4, cursor: 'pointer',
                  fontSize: 10, fontFamily: 'monospace', padding: '1px 7px', whiteSpace: 'nowrap',
                }}
              >
                ▶ rouvrir la connexion
              </button>
            )}
            {/* La régie publie son programme en PERMANENCE dès qu'elle a une image :
                c'est ce qui rend le retour des téléphones disponible hors antenne et
                le passage à l'antenne immédiat. Mais une régie laissée ouverte
                continuerait d'émettre pour personne — d'où cette fermeture à la main.

                Barrée quand la source à l'antenne EST la régie : fermer couperait
                l'émission, et un bouton de panneau de diagnostic n'a pas à pouvoir
                faire ça sans le dire. */}
            {programLinkActive && (
              <button
                onClick={() => { if (!programLinkOnAir) onCloseProgramLink?.(); }}
                disabled={programLinkOnAir}
                title={programLinkOnAir
                  ? "La régie est à l'antenne : basculez la source avant de fermer la connexion."
                  : "Ferme la publication permanente du programme. Les téléphones repasseront en retour direct ; le bouton « rouvrir la connexion » prendra la place de celui-ci."}
                style={{
                  background: programLinkOnAir ? 'rgba(148,163,184,0.10)' : 'rgba(248,113,113,0.15)',
                  border: `1px solid ${programLinkOnAir ? 'rgba(148,163,184,0.25)' : 'rgba(248,113,113,0.45)'}`,
                  color: programLinkOnAir ? 'rgba(148,163,184,0.55)' : '#fca5a5',
                  borderRadius: 4, cursor: programLinkOnAir ? 'not-allowed' : 'pointer',
                  fontSize: 10, fontFamily: 'monospace', padding: '1px 7px', whiteSpace: 'nowrap',
                }}
              >
                {programLinkOnAir ? '● à l\'antenne' : '⏹ fermer la connexion'}
              </button>
            )}
          </div>
          {reading?.lien.rttMs == null && reading?.lien.availableOutBps == null ? (
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(148,163,184,0.7)' }}>
              Aucune connexion WebRTC ouverte — rien à mesurer sans en ouvrir une.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap', fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0' }}>
              <span>aller-retour <b style={{ color: '#38bdf8' }}>{reading!.lien.rttMs != null ? `${Math.round(reading!.lien.rttMs)} ms` : '—'}</b></span>
              <span title="Estimation du contrôle de congestion du navigateur POUR CETTE CONNEXION, trafic en cours compris — pas la capacité de la ligne. Elle ne se découvre qu'en émettant : une connexion au repos reste au plancher de démarrage, autour de 300 kb/s.">
                flux montant estimé <b style={{ color: reading!.lien.significatif ? '#4ade80' : 'rgba(148,163,184,0.75)' }}>
                  {reading!.lien.availableOutBps != null ? formatBps(reading!.lien.availableOutBps) : '—'}
                </b>
                <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.8)' }}>
                  {' '}sur une connexion qui émet {reading!.lien.emisBps != null ? formatBps(reading!.lien.emisBps) : '—'}
                </span>
              </span>
              {!reading!.lien.significatif && (
                <span style={{ fontSize: 10, color: '#fbbf24' }}>
                  ⚠ non significatif — rien n'émet assez pour sonder le lien
                </span>
              )}
              <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.8)' }}>
                {reading!.lien.chemin === 'relais' ? 'par relais TURN' : 'chemin direct'}
                {reading!.lien.adresse ? ` · ${reading!.lien.adresse}` : ''}
                {reading!.lien.mesureeSur ? ` · via ${reading!.lien.mesureeSur}` : ''}
              </span>
              <button
                onClick={() => logIcePairs().then(setPairesIce)}
                title="Liste les chemins ICE tentés et ce qu'ils ont répondu, et l'écrit dans les journaux. Une paire directe qui envoie sans jamais recevoir dit que le port n'est pas joignable de l'extérieur."
                style={{
                  background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(56,189,248,0.4)',
                  color: '#7dd3fc', borderRadius: 4, cursor: 'pointer',
                  fontSize: 10, fontFamily: 'monospace', padding: '1px 7px',
                }}
              >
                pourquoi ce chemin ?
              </button>
            </div>
          )}
          {pairesIce && (
            <div style={{ marginTop: 5, maxHeight: 120, overflowY: 'auto', fontFamily: 'monospace', fontSize: 10, lineHeight: 1.5 }}>
              {pairesIce.length === 0 && <div style={{ color: 'rgba(148,163,184,0.7)' }}>aucune paire — aucune connexion ouverte</div>}
              {pairesIce.map((p, i) => (
                <div key={i} style={{ color: p.retenue ? '#4ade80' : p.reponsesRecues === 0 && p.requetesEnvoyees > 0 ? '#fca5a5' : 'rgba(148,163,184,0.8)' }}>
                  {p.connexion} · {p.local} → {p.distant} {p.adresseDistante} · {p.etat}
                  {p.retenue ? ' · RETENUE' : ''} · env. {p.requetesEnvoyees} / reçues {p.reponsesRecues}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: '1 1 320px', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 6, padding: '6px 10px', background: 'rgba(148,163,184,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 9, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'rgba(148,163,184,0.85)' }}>
              Connexion de cette machine
            </span>
            <span style={{ flex: 1 }} />
            <select
              value={cible}
              onChange={e => { setCible(e.target.value as 'internet' | 'cast'); setArme(false); }}
              title="« vers cast » emprunte le trajet du média (443, HAProxy). « Internet » juge la ligne elle-même."
              style={{ background: 'rgba(15,23,42,0.9)', color: '#cbd5e1', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 4, fontSize: 10, fontFamily: 'monospace', padding: '1px 4px' }}
            >
              <option value="cast">vers cast</option>
              <option value="internet">Internet</option>
            </select>
            <button
              onClick={lancerMesure}
              disabled={mesureEnCours}
              title="Mesure réelle par speed.cloudflare.com. Elle dépense 10 Mo en descente et 5 Mo en montée : pendant une émission, elle les prend à l'antenne."
              style={{
                background: arme ? 'rgba(244,63,94,0.25)' : 'rgba(56,189,248,0.15)',
                border: `1px solid ${arme ? 'rgba(244,63,94,0.6)' : 'rgba(56,189,248,0.4)'}`,
                color: arme ? '#fda4af' : '#7dd3fc', borderRadius: 4, cursor: mesureEnCours ? 'wait' : 'pointer',
                fontSize: 10, fontFamily: 'monospace', padding: '2px 8px',
              }}
            >
              {mesureEnCours ? 'mesure…' : arme ? 'confirmer — consomme 15 Mo' : 'mesurer'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap', fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0' }}>
            {mesure ? (
              <>
                <span>mesuré ↓ <b style={{ color: '#38bdf8' }}>{formatBps(mesure.downBps)}</b></span>
                <span>↑ <b style={{ color: '#f472b6' }}>{formatBps(mesure.upBps)}</b></span>
                {mesure.upBpsServeur != null && (
                  <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.85)' }}
                        title="Montée telle que le serveur l'a chronométrée. Nettement plus basse que celle du navigateur, elle désigne le serveur — HAProxy puis le microservice — et non la ligne.">
                    ↑ vu du serveur {formatBps(mesure.upBpsServeur)}
                  </span>
                )}
              </>
            ) : (
              <span style={{ color: 'rgba(148,163,184,0.7)' }}>aucune mesure lancée</span>
            )}
            <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.8)' }}>
              estimation du navigateur :{' '}
              {estimation.estimationDownBps != null ? `↓ ${formatBps(estimation.estimationDownBps)}` : 'non exposée'}
              {estimation.estimationRttMs != null ? ` · ${estimation.estimationRttMs} ms` : ''}
              {estimation.qualite ? ` · ${estimation.qualite}` : ''}
            </span>
          </div>
          {erreurMesure && (
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#fda4af', marginTop: 2 }}>
              échec : {erreurMesure}
            </div>
          )}
        </div>
      </div>

      {/* ── Ce que la régie envoie à MediaMTX ───────────────────────────────
          Le pendant, juste au-dessus, de la section suivante : la même
          présentation (un tuyau par ligne), pour pouvoir comparer d'un coup
          d'œil ce qui part et ce qui arrive. Mesuré ICI, dans le navigateur —
          pas par le microservice, contrairement à « Reçu ». */}
      {envoyesMediaMtx.length > 0 && (
        <div style={{ padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <div style={{ fontSize: 9, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'rgba(148,163,184,0.85)', marginBottom: 3 }}>
            Envoyé à MediaMTX (mesuré ici, navigateur)
          </div>
          {envoyesMediaMtx.map(p => (
            <div key={p.id} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontFamily: 'monospace', fontSize: 11, color: '#e2e8f0', lineHeight: 1.6 }}>
              <span style={{ minWidth: 90, color: '#a78bfa' }}>{p.kind}:{p.label}</span>
              <span>{formatBps(p.outBps)}</span>
              <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.8)' }}>
                cumul {formatBytes(p.outBytes)}
              </span>
              {/* Coût d'ENCODAGE de CE tuyau précis, pas une moyenne globale : un
                  angle qui coûte cher à encoder ne doit pas se cacher derrière un
                  chiffre unique qui mélangerait tout. */}
              <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.8)' }}
                    title="Millisecondes de CPU par seconde passées à ENCODER ce tuyau précis. 1000 ms/s = un cœur entier.">
                cpu {p.encodeMsParSec != null ? `${Math.round(p.encodeMsParSec)} ms/s` : '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Ce que MediaMTX reçoit ──────────────────────────────────────────
          Le navigateur ne connaît que ce qu'il a ÉMIS. La différence entre les
          deux colonnes est ce qui s'est perdu en route, et c'est la seule mesure
          qui accuse le lien sans se tromper. */}
      {recu?.available && recu.sessions?.length > 0 && (
        <div style={{ padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <div style={{ fontSize: 9, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'rgba(148,163,184,0.85)', marginBottom: 3 }}>
            Reçu par MediaMTX (via microservice)
          </div>
          {recu.sessions.map((s: any) => (
            <div key={s.id} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontFamily: 'monospace', fontSize: 11, color: '#e2e8f0', lineHeight: 1.6 }}>
              <span style={{ minWidth: 90, color: '#a78bfa' }}>{s.role}</span>
              <span>{s.debit_recu_bps != null ? formatBps(s.debit_recu_bps) : '—'}</span>
              {/* Le cumul depuis l'ouverture de la session ne dit rien à lui seul :
                  quelques paquets perdus sur des millions sont la vie normale
                  d'Internet, et WebRTC les redemande. Ce qui compte est la perte
                  SUR L'INTERVALLE — c'est elle, et elle seule, qui s'allume. */}
              <span style={{ color: s.paquets_perdus_intervalle > 0 ? '#f87171' : 'rgba(148,163,184,0.8)' }}>
                {s.paquets_perdus} perdu{s.paquets_perdus > 1 ? 's' : ''}
                {s.paquets_recus > 0 && (
                  <span style={{ opacity: 0.75 }}> / {s.paquets_recus} ({((s.paquets_perdus / (s.paquets_recus + s.paquets_perdus)) * 100).toFixed(3)} %)</span>
                )}
                {s.paquets_perdus_intervalle > 0 && <b> · +{s.paquets_perdus_intervalle} à l'instant</b>}
              </span>
              <span style={{ fontSize: 10, color: s.chemin === 'relais' ? '#fbbf24' : 'rgba(148,163,184,0.8)' }}>
                {s.chemin}
              </span>
              <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.6)' }}>{s.path}</span>
            </div>
          ))}
        </div>
      )}

      <div style={scrollAll ? { flexShrink: 0 } : { flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {pipes.length === 0 ? (
          <div style={{ padding: 16, fontFamily: 'monospace', fontSize: 11, color: 'rgba(148,163,184,0.6)' }}>
            Aucun tuyau ouvert — ni appareil connecté, ni flux distant tiré.
            Une source posée sur le deck ne consomme rien tant qu'elle n'est ni en
            preview ni à l'antenne : elle n'apparaît ici qu'une fois réellement tirée.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={head}>Tuyau</th>
                <th style={head}>Nature</th>
                <th style={head}>Trajet</th>
                <th style={{ ...head, textAlign: 'right' }}>↓ entrant</th>
                <th style={{ ...head, textAlign: 'right' }}>↑ sortant</th>
                <th style={{ ...head, textAlign: 'right' }}>Cumul</th>
                <th style={head}>Définition</th>
                <th style={{ ...head, textAlign: 'right' }}>CPU codec</th>
                <th style={head}>Bridé par</th>
                <th style={head}>État</th>
              </tr>
            </thead>
            <tbody>
              {pipes.map(p => {
                const dormant = p.inBps < 1_000 && p.outBps < 1_000;
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', opacity: dormant ? 0.45 : 1 }}>
                    <td style={{ ...cell, color: '#e2e8f0', fontWeight: 600 }}>{p.label || '—'}</td>
                    <td style={{ ...cell, color: p.family === 'hls' ? '#a78bfa' : '#22d3ee' }}>{p.kind}</td>
                    {/* Par où passe ce tuyau. Une partie du parc peut être en SFU
                        et l'autre en direct : sans cette colonne, rien à l'écran ne
                        le disait, et « whep » ne parle qu'à qui connaît le code. */}
                    <td style={{ ...cell, fontSize: 10, fontWeight: 700,
                                 color: p.trajet === 'mediamtx' ? '#fbbf24'
                                      : p.trajet === 'direct'   ? '#4ade80'
                                      : 'rgba(148,163,184,0.45)' }}
                        title={p.trajet === 'mediamtx'
                          ? 'Le média transite par MediaMTX — un étage de plus, et de la latence'
                          : p.trajet === 'direct'
                            ? 'Le pair est en face, rien ne s\'interpose'
                            : 'Trajet inconnu pour ce type de tuyau'}>
                      {p.trajet === 'mediamtx' ? 'SERVEUR' : p.trajet === 'direct' ? 'DIRECT' : '—'}
                    </td>
                    <td style={{ ...cell, textAlign: 'right', color: p.inBps  > 1_000 ? '#38bdf8' : 'rgba(148,163,184,0.5)' }}>{formatBps(p.inBps)}</td>
                    <td style={{ ...cell, textAlign: 'right', color: p.outBps > 1_000 ? '#f472b6' : 'rgba(148,163,184,0.5)' }}>{formatBps(p.outBps)}</td>
                    <td style={{ ...cell, textAlign: 'right', color: 'rgba(148,163,184,0.8)' }}>{formatBytes(p.inBytes + p.outBytes)}</td>
                    {/* Source → émis. Un écart entre les deux désigne l'ENCODEUR,
                        qui a réduit ce que la caméra donnait — et non la caméra. */}
                    <td style={{ ...cell, color: p.sourceDef && p.emisDef && p.sourceDef !== p.emisDef ? '#fbbf24' : 'rgba(148,163,184,0.85)' }}>
                      {p.emisDef
                        ? (p.sourceDef && p.sourceDef !== p.emisDef
                            ? `${p.sourceDef} → ${p.emisDef}`
                            : p.emisDef)
                        : (p.sourceDef ?? '—')}
                      {p.emisFps != null ? <span style={{ opacity: 0.7 }}> @{Math.round(p.emisFps)}</span> : null}
                    </td>
                    {/* Encodage et décodage additionnés : c'est ce que CE tuyau
                        coûte à la machine, quel que soit son sens. */}
                    <td style={{ ...cell, textAlign: 'right', color: 'rgba(148,163,184,0.85)' }}>
                      {p.encodeMsParSec == null && p.decodeMsParSec == null
                        ? '—'
                        : `${Math.round((p.encodeMsParSec ?? 0) + (p.decodeMsParSec ?? 0))} ms/s`}
                    </td>
                    <td style={{ ...cell, color: p.limitation && p.limitation !== 'none' ? '#fbbf24' : 'rgba(148,163,184,0.5)' }}>
                      {p.limitation && p.limitation !== 'none' ? p.limitation : '—'}
                    </td>
                    <td style={{ ...cell, color: p.state === 'connected' ? '#4ade80' : 'rgba(148,163,184,0.7)' }}>{p.state ?? 'hls'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
});

// ─── MultiviewPanelV2 ─────────────────────────────────────────────────────────

type Col1View = 'multiview' | 'diffusionFluxFinal' | 'intercomm' | 'carte' | 'debits';

// `microserviceUrl` : façade `{serveur}/{uuid}/regie`, seule voie vers l'endpoint
// de qualité de lien du microservice de la chaîne.

// Passage de relais des deux moniteurs vidéo entre deux montages du panneau (régie ↔ onglet
// détaché). Les <video> sont recréés, mais leur flux n'est affecté par la régie qu'au montage
// initial et quand il CHANGE : sans ce relais le nouvel élément reste noir (le preview revenait
// au clic, qui réaffecte son flux ; le flux final, jamais).
// Hauteur réelle du panneau complet dans la régie, mesurée en continu. Le mode `sideOnly` (multiview dans
// un autre onglet) la reprend À L'IDENTIQUE : l'ouverture de la seconde fenêtre ne doit pas bouger d'un
// pixel le bloc central (un calcul à partir de la vidéo preview tombait 4 à 5 px trop bas).
let lastFullPanelHeight: number | null = null;

let videoHandoff: { final: MediaProvider | null; preview: MediaProvider | null } | null = null;

export const MultiviewPanelV2: React.FC<MultiviewPanelV2Props> = React.memo(({
  microserviceUrl,
  canvasPreviewRef,
  isStreaming,
  deckPreviewVideoRef,
  deckPreviewLabel,
  deckPreviewSourceId,
  activeJingleId,
  previewMuted,
  onTogglePreviewMuted,
  finalMuted,
  onToggleFinalMuted,
  deckSlots,
  previewStreams,
  onDeckPreview,
  onDeckSwitch,
  onAcquirePreview,
  selectedVideoSource,
  channelActive,
  poweredOffSlots,
  availableSources,
  layoutSlots,
  slotConfigs,
  onSlotConfigsChange,
  onSlotsReorder,
  closeOnFullscreen,
  onToggleCloseOnFullscreen,
  onOpenFullscreen,
  sourceAliases,
  streamSource,
  defaultStreamSource,
  handleStreamSourceChange,
  handleDefaultStreamSourceChange,
  channelUuid,
  hlsVolume,
  hlsMuted,
  onHlsVolumeChange,
  onHlsMutedChange,
  monitorHlsUrl,
  broadcastingChannel,
  hlsMonitorStream,
  hlsAudioRegistry,
  hlsAudioSourceId,
  onHlsReset,
  remoteDevices,
  localMics,
  intercommMicStream,
  intercommDeviceStreams,
  intercommSpeakingDevices,
  onIntercommActivate,
  onIntercommMicGainChange,
  onIntercommMicChange,
  deviceGeo,
  onGeoWatch,
  onMetricsLog,
  programLinkActive, programLinkOnAir, onCloseProgramLink, onReopenProgramLink,
  sideOnly = false,
}) => {

  // Rendu dans un onglet détaché : plus de bouton de détachement, et multiview en haut,
  // preview + flux final en bas.
  const inDetachedTab = useContext(PortalTargetContext) !== null;
  // Chat ouvert sur la chaîne (chatModeration -1 = non autorisé) : l'onglet détaché cale les vidéos
  // à gauche et réserve la colonne de droite au logo et au fil du chat.
  const withChat = inDetachedTab && broadcastingChannel.chatModeration >= 0;
  useLayoutEffect(() => {
    if (sideOnly) return;
    const h = videoHandoff;
    videoHandoff = null;
    if (h) {
      const apply = (el: HTMLVideoElement | null, src: MediaProvider | null) => {
        if (el && src && el.srcObject !== src) { el.srcObject = src; el.play().catch(() => {}); }
      };
      apply(canvasPreviewRef.current, h.final);
      apply(deckPreviewVideoRef.current, h.preview);
    }
    // Fermer l'onglet détruit son document AVANT que React ne démonte le panneau : à ce moment
    // `srcObject` est déjà vide. On garde donc le dernier flux vu, relevé en continu et à
    // `pagehide`, qui part avant la destruction.
    const last: { final: MediaProvider | null; preview: MediaProvider | null } = { final: null, preview: null };
    const snap = () => {
      const f = canvasPreviewRef.current?.srcObject;
      const v = deckPreviewVideoRef.current?.srcObject;
      if (f) last.final = f;
      if (v) last.preview = v;
    };
    snap();
    const timer = window.setInterval(snap, 500);
    const pageWin = (canvasPreviewRef.current ?? deckPreviewVideoRef.current)?.ownerDocument.defaultView ?? null;
    pageWin?.addEventListener('pagehide', snap);
    return () => {
      window.clearInterval(timer);
      try { pageWin?.removeEventListener('pagehide', snap); } catch { /* fenêtre déjà détruite */ }
      snap();
      const mine = { final: last.final, preview: last.preview };
      videoHandoff = mine;
      // Pas de remontage derrière (régie quittée) : ne rien garder.
      setTimeout(() => { if (videoHandoff === mine) videoHandoff = null; }, 1000);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [col1View,       setCol1View]       = useState<Col1View>('multiview');

  const [canvasHasSignal,  setCanvasHasSignal]  = useState(false);
  const [previewHasSignal, setPreviewHasSignal] = useState(false);

  const [previewVideoHeight, setPreviewVideoHeight] = useState<number | null>(null);
  const [previewVideoWidth,  setPreviewVideoWidth]  = useState<number | null>(null);
  const previewVideoContainerRef = useRef<HTMLDivElement>(null);
  const sideRef = useRef<HTMLDivElement>(null);
  const fullPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = fullPanelRef.current;
    if (sideOnly || inDetachedTab || !el) return;
    const RO = ((el.ownerDocument.defaultView as (Window & typeof globalThis) | null) ?? window).ResizeObserver;
    const ro = new RO(() => { const h = el.getBoundingClientRect().height; if (h > 0) lastFullPanelHeight = h; });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sideOnly, inDetachedTab]);

  // Mode `sideOnly` (multiview dans un autre onglet) : il n'y a plus de colonne Preview à mesurer, or
  // Intercomm et Diffusion calent leurs colonnes sur la largeur de la vidéo preview (à défaut, elles
  // retombent sur des proportions par défaut). On recalcule donc la largeur qu'elle AURAIT dans le
  // panneau complet : grille 2fr 1fr 1fr, gouttières 4, vidéo = colonne − bordures (3) − marges (2).
  useEffect(() => {
    const el = sideRef.current;
    if (!sideOnly || !el) return;
    const RO = ((el.ownerDocument.defaultView as (Window & typeof globalThis) | null) ?? window).ResizeObserver;
    const ro = new RO(entries => {
      const w = Math.max(0, Math.round((entries[0].contentRect.width - 8) / 4 - 5));
      const h = Math.round(w * 9 / 16);
      setPreviewVideoWidth (prev => prev === w ? prev : w);
      setPreviewVideoHeight(prev => prev === h ? prev : h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sideOnly]);

  const intervalAudienceDataRef  = useRef<NodeJS.Timeout | null>(null);
  const [simultaneousConnections, setSimultaneousConnections] = useState(0);

  useEffect(() => {
    const el = previewVideoContainerRef.current;
    if (!el) return;
    const RO = ((el.ownerDocument.defaultView as (Window & typeof globalThis) | null) ?? window).ResizeObserver;
    const ro = new RO(entries => {
      const h = Math.round(entries[0].contentRect.height);
      const w = Math.round(entries[0].contentRect.width);
      setPreviewVideoHeight(prev => prev === h ? prev : h);
      setPreviewVideoWidth (prev => prev === w ? prev : w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = canvasPreviewRef.current;
    if (!el) return;
    const onPlay    = () => setCanvasHasSignal(true);
    const onEmptied = () => setCanvasHasSignal(false);
    el.addEventListener('play', onPlay);
    el.addEventListener('emptied', onEmptied);

    // `emptied` est le seul signal de perte de signal du moniteur final. Il part
    // dès qu'on réaffecte srcObject, MÊME à valeur identique — c'est ce qui
    // produisait un flash noir à chaque bascule de source, le cache ci-dessous
    // venant s'ajouter au <video> vidé. Tout code qui pose srcObject sur cet
    // élément doit donc tester `!==` d'abord.
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('emptied', onEmptied);
    };
  }, [canvasPreviewRef]);

  useEffect(() => {
    const el = deckPreviewVideoRef.current;
    if (!el) return;
    const onPlay    = () => setPreviewHasSignal(true);
    const onEmptied = () => setPreviewHasSignal(false);
    el.addEventListener('play', onPlay);
    el.addEventListener('emptied', onEmptied);
    return () => { el.removeEventListener('play', onPlay); el.removeEventListener('emptied', onEmptied); };
  }, [deckPreviewVideoRef]);

  // Tenu ici et non dans chaque mini-vue : c'est le panneau qui sait laquelle est
  // prise et laquelle est survolée, et une vignette ne peut pas connaître l'état
  // de ses voisines.
  const [draggedSlot, setDraggedSlot] = useState<number | null>(null);
  const [dropTarget,  setDropTarget]  = useState<number | null>(null);

  const handleDragStateChange = useCallback((dragging: number | null, over: number | null) => {
    if (dragging !== null || (dragging === null && over === null)) setDraggedSlot(dragging);
    setDropTarget(over);
  }, []);

  const resolvedSlots: (VideoSource | null)[] = Array.from({ length: SLOT_COUNT }, (_, i) => {
    const cfg = slotConfigs[i];
    if (cfg?.sourceId) {
      const found = availableSources.find(s => s.id === cfg.sourceId);
      if (found) return found;
      if (cfg.sourceId.startsWith('scene-')) {
        const layoutId = cfg.sourceId.slice('scene-'.length);
        const layout   = layoutSlots.find(l => l.id === layoutId);
        if (layout) return { id: cfg.sourceId, label: layout.label, type: 'scene' as const };
      }
      return null;
    }
    return deckSlots[i] ?? null;
  });

  const fetchAudienceData = useCallback(async () => {
    if (!channelUuid) { setSimultaneousConnections(0); return; }
    try {
      const result = await getHeartBeatCount(undefined, channelUuid, 'live', '1 minute');
      setSimultaneousConnections(result.code === 1 ? result.payload.ipCount : 0);
    } catch {}
  }, [channelUuid]);

  useEffect(() => {
    fetchAudienceData();
    intervalAudienceDataRef.current = setInterval(fetchAudienceData, 60_000);
    return () => { if (intervalAudienceDataRef.current) clearInterval(intervalAudienceDataRef.current); };
  }, [fetchAudienceData]);

  const colHeaderStyle: React.CSSProperties = {
    padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 5,
    borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
    height: 38, boxSizing: 'border-box',
  };

  const btnStyle: React.CSSProperties = {
    background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 5,
    cursor: 'pointer', padding: '3px 5px', display: 'flex', alignItems: 'center',
    gap: 3, color: 'rgba(148,163,184,0.7)',
  };

  const tabBtnStyle = (active: boolean, accentColor?: string): React.CSSProperties => {
    const accentBg   = accentColor ? `${accentColor}2e` : 'rgba(99,102,241,0.18)';
    const accentBord = accentColor ? `${accentColor}80` : 'rgba(99,102,241,0.5)';
    const accentText = accentColor ?? '#a5b4fc';
    return {
      background  : active ? accentBg   : 'rgba(255,255,255,0.05)',
      border      : `1px solid ${active ? accentBord : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 5, cursor: 'pointer', padding: '3px 8px',
      display: 'flex', alignItems: 'center', gap: 4,
      color   : active ? accentText : 'rgba(148,163,184,0.6)',
      fontSize: 10, fontFamily: 'monospace', fontWeight: 700,
      transition: 'background 0.15s, border-color 0.15s, color 0.15s',
    };
  };

  const retourView = (
    <RetourView
      streamSource                    = {streamSource}
      defaultStreamSource             = {defaultStreamSource}
      handleStreamSourceChange        = {handleStreamSourceChange}
      handleDefaultStreamSourceChange = {handleDefaultStreamSourceChange}
      videoHeight                     = {previewVideoHeight}
      videoWidth                      = {previewVideoWidth}
      simultaneousConnections         = {simultaneousConnections}
      hlsVolume                       = {hlsVolume}
      hlsMuted                        = {hlsMuted}
      onHlsVolumeChange               = {onHlsVolumeChange}
      onHlsMutedChange                = {onHlsMutedChange}
      monitorHlsUrl                   = {monitorHlsUrl}
      channelActive                   = {channelActive}
      broadcastingChannel             = {broadcastingChannel}
      hlsMonitorStream                = {hlsMonitorStream}
      hlsAudioRegistry                = {hlsAudioRegistry}
      hlsAudioSourceId                = {hlsAudioSourceId}
    />
  );

  const intercommView = (
    <IntercommView
      hlsVolume                = {hlsVolume}
      hlsMuted                 = {hlsMuted}
      onHlsVolumeChange        = {onHlsVolumeChange}
      onHlsMutedChange         = {onHlsMutedChange}
      hlsAudioRegistry         = {hlsAudioRegistry}
      hlsAudioSourceId         = {hlsAudioSourceId}
      remoteDevices            = {remoteDevices}
      previewStreams            = {previewStreams}
      localMics                = {localMics}
      screenWidth              = {previewVideoWidth}
      screenHeight             = {previewVideoHeight}
      intercommMicStream       = {intercommMicStream}
      intercommDeviceStreams    = {intercommDeviceStreams}
      intercommSpeakingDevices = {intercommSpeakingDevices}
      onIntercommActivate      = {onIntercommActivate}
      onIntercommMicGainChange = {onIntercommMicGainChange}
      onMicChange              = {onIntercommMicChange}
    />
  );

  if (sideOnly) {
    const frame: React.CSSProperties = {
      display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
      border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: 7, background: 'rgba(0,0,0,0.25)',
    };
    return (
      <div ref={sideRef} style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 4, padding: 6, boxSizing: 'border-box',
        // Hauteur mesurée du panneau complet (voir `lastFullPanelHeight`) ; sinon estimation à partir de la vidéo.
        height: lastFullPanelHeight ?? (previewVideoHeight != null ? previewVideoHeight + 64 : 340),
        background: 'var(--color-Panel-niv1-BG)', borderRadius: 10, border: '1px solid var(--color-Panel-niv1-Border)',
      }}>
        <div style={frame}>
          <div style={colHeaderStyle}>
            <span style={{ ...tabBtnStyle(true, '#f59e0b'), cursor: 'default' }}><Phone style={{ width: 11, height: 11 }} /> Intercomm</span>
          </div>
          <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>{intercommView}</div>
        </div>
        <div style={frame}>
          <div style={colHeaderStyle}>
            <span style={{ ...tabBtnStyle(true), cursor: 'default' }}><Cast style={{ width: 11, height: 11 }} /> Diffusion du flux final</span>
            <div style={{ flex: 1 }} />
            <button style={btnStyle} title="Réinitialiser la connexion HLS" onClick={() => onHlsReset?.()}>
              <RefreshCw style={{ width: 13, height: 13 }} />
            </button>
          </div>
          <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>{retourView}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={fullPanelRef} style={{
        display: 'flex', flexDirection: 'column',
        background: 'var(--color-Panel-niv1-BG)', backdropFilter: 'blur(4px)',
        borderRadius: 10, border: '1px solid var(--color-Panel-niv1-Border)',
        overflow: 'hidden', height: '100%',
      }}>

        {/* ══ CORPS — grille 3 colonnes (2fr 1fr 1fr) ══ */}
        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}>
        <div style={{
          display: 'grid',
          // Onglet détaché : en haut preview + flux final côte à côte, chacun d'une hauteur de
          // ½ fenêtre ; en dessous 2 rangées de 4 mini-vues d'une hauteur de ¼ de fenêtre. Tout est
          // centré. Les largeurs viennent des hauteurs (16/9) ; les constantes en px sont les cadres,
          // en-têtes et gouttières déduits (marge 8×2 + cadre 2 + grille 12 = 30 ; carte : en-tête 38
          // + marge 5 + bordures 3 ; bloc mini-vues : bordures 3 + en-tête 38 + marges 10 + gouttière 4).
          gridTemplateColumns: inDetachedTab ? 'repeat(2, calc((50vh - 61px) * 16 / 9 + 5px))' : '2fr 1fr 1fr',
          ...(inDetachedTab ? { justifyContent: withChat ? 'start' : 'center', alignContent: 'start' } : {}),
          ...(inDetachedTab ? { gridTemplateRows: 'auto auto' } : {}),
          gap: 4, padding: 6, minHeight: 0,
          ...(withChat ? { flex: '0 0 auto' } : { flex: 1 }),
        }}>

          {/* ─── COL 1 : Multiview / Retour / Intercomm ─── */}
          <div style={{
            display: 'flex', flexDirection: 'column',
            border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: 7,
            overflow: 'hidden', background: 'rgba(0,0,0,0.25)',
            // Hauteur du bloc = en-tête 38 + marges 10 + 2 rangées de mini-vues + gouttière 4 + bordures 3 ;
            // Carte et Débits la remplissent à leur tour.
            ...(inDetachedTab ? { gridColumn: '1 / -1', gridRow: 2, minHeight: 0, height: 'calc(50vh - 19px)' } : {}),
          }}>
            {/* Header onglets */}
            <div style={colHeaderStyle}>
              <button style={tabBtnStyle(col1View === 'multiview')} onClick={() => setCol1View('multiview')} title="Grille multiview">
                <LayoutGrid style={{ width: 11, height: 11 }} />
                Multiview
              </button>
              {!inDetachedTab && (
              <button style={tabBtnStyle(col1View === 'diffusionFluxFinal')} onClick={() => setCol1View('diffusionFluxFinal')} title="Diffusion du flux final">
                <Cast style={{ width: 11, height: 11 }} />
                Diffusion du flux final
              </button>
              )}
              {!inDetachedTab && (
              <button style={tabBtnStyle(col1View === 'intercomm', '#f59e0b')} onClick={() => setCol1View('intercomm')} title="Intercomm">
                <Phone style={{ width: 11, height: 11 }} />
                Intercomm
              </button>
              )}
              <button style={tabBtnStyle(col1View === 'carte', '#22d3ee')} onClick={() => setCol1View('carte')} title="Position et visée des appareils distants">
                <MapPin style={{ width: 11, height: 11 }} />
                Carte
              </button>
              <button style={tabBtnStyle(col1View === 'debits', '#38bdf8')} onClick={() => setCol1View('debits')} title="Débits : ce que la régie reçoit et émet, tuyau par tuyau">
                <Activity style={{ width: 11, height: 11 }} />
                Débits
              </button>


              <div style={{ flex: 1 }} />

              {col1View === 'multiview' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {!inDetachedTab && (
                    <button style={btnStyle} title={closeOnFullscreen ? 'Ouvrir en plein écran (ferme ce panneau)' : 'Ouvrir en plein écran'} onClick={() => onOpenFullscreen(closeOnFullscreen)}>
                      <ExternalLink style={{ width: 13, height: 13 }} />
                      {closeOnFullscreen && <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(148,163,184,0.5)', lineHeight: 1 }}>×</span>}
                    </button>
                  )}
                  <button style={btnStyle} title="Configurer le multiview" onClick={() => setIsSettingsOpen(true)}>
                    <Settings style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              )}
              {col1View === 'diffusionFluxFinal' && (
                <button style={btnStyle} title="Réinitialiser la connexion HLS" onClick={() => onHlsReset?.()}>
                  <RefreshCw style={{ width: 13, height: 13 }} />
                </button>
              )}
            </div>

            {/* Multiview — toujours monté, masqué si autre vue */}
            <div style={{
              display: col1View === 'multiview' ? 'grid' : 'none',
              // Onglet détaché : 2 rangées de 4, cellules 16/9 d'un quart de la hauteur de fenêtre.
              ...(inDetachedTab
                ? {
                    gridTemplateColumns: 'repeat(4, calc((25vh - 37px) * 16 / 9))',
                    gridAutoRows: 'calc(25vh - 37px)',
                    alignContent: 'start', justifyContent: 'center',
                  }
                : { gridTemplateColumns: 'repeat(4, 1fr)', gridAutoRows: '1fr' }),
              gap: 4, padding: 5, flex: 1, minHeight: 0,
            }}>
              {resolvedSlots.slice(0, 8).map((slot, i) => (
                <MiniScreen
                  key={i}
                  slot={slot}
                  index={i}
                  stream={previewStreams.get(slot?.id ?? '')}
                  isPreview={!!slot && deckPreviewSourceId === slot.id}
                  isLive={!!slot && selectedVideoSource === slot.id}
                  customLabel={slotConfigs[i]?.customLabel ?? null}
                  sourceAliases={sourceAliases}
                  onDeckPreview={onDeckPreview}
                  onDeckSwitch={onDeckSwitch}
                  onAcquirePreview={onAcquirePreview}
                  channelActive={channelActive}
                  onReorder={onSlotsReorder}
                  isDragged={draggedSlot === i}
                  isDropTarget={dropTarget === i && draggedSlot !== i}
                  onDragStateChange={handleDragStateChange}
                  isPoweredOff={!!slot && !!poweredOffSlots?.has(slot.id)}
                />
              ))}
            </div>

            {/* RetourView — toujours montée */}
            {!inDetachedTab && <div style={{ display: col1View === 'diffusionFluxFinal' ? 'flex' : 'none', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {retourView}
            </div>}

            {/* IntercommView — toujours montée */}
            {!inDetachedTab && <div style={{ display: col1View === 'intercomm' ? 'flex' : 'none', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {intercommView}
            </div>}

            {/* CarteView — montée seulement quand l'onglet est actif : maplibre
                instancie un contexte WebGL, inutile de le porter en permanence. */}
            {col1View === 'carte' && (
              <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <CarteView remoteDevices={remoteDevices} deviceGeo={deviceGeo} onGeoWatch={onGeoWatch} />
              </div>
            )}

            {/* Débits — montée à la demande comme la carte. La MESURE, elle, tourne
                en permanence : c'est sa trace dans les journaux qui compte, et elle
                doit exister avant qu'on pense à ouvrir l'onglet. */}
            {col1View === 'debits' && (
              <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <DebitsView microserviceUrl={microserviceUrl} remoteDevices={remoteDevices} onMetricsLog={onMetricsLog}
                  programLinkActive={programLinkActive} programLinkOnAir={programLinkOnAir} onCloseProgramLink={onCloseProgramLink}
                  onReopenProgramLink={onReopenProgramLink} scrollAll={inDetachedTab} />
              </div>
            )}
          </div>
          {/* fin COL 1 */}

          {/* ─── COL 2 : Preview actif ─── */}
          <div style={{
            display: 'flex', flexDirection: 'column',
            border: '1.5px solid #22c55e', borderRadius: 7,
            overflow: 'hidden', background: 'rgba(0,0,0,0.3)',
            ...(inDetachedTab ? { gridColumn: 1, gridRow: 1 } : {}),
          }}>
            <div style={colHeaderStyle}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 5px #22c55e', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: '#86efac', fontFamily: 'monospace', flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {(() => {
                  const src   = deckPreviewSourceId ? availableSources.find(s => s.id === deckPreviewSourceId) : null;
                  const label = src ? cleanLabel(resolveSourceLabel(src, sourceAliases)) : null;
                  return label ? `PRV — ${label}` : 'Preview — —';
                })()}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                {activeJingleId && (
                  <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: '0.4px', color: '#fff', background: 'rgba(234,88,12,0.85)', borderRadius: 3, padding: '1px 4px', animation: 'mvPulse 1s ease-in-out infinite' }}>🎞 JINGLE</span>
                )}
                <button onClick={onTogglePreviewMuted} title={previewMuted ? 'Activer le son preview' : 'Couper le son preview'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: previewMuted ? 'rgba(255,255,255,0.35)' : '#4ade80', display: 'flex', alignItems: 'center' }}>
                  {previewMuted ? <VolumeX style={{ width: 11, height: 11 }} /> : <Volume2 style={{ width: 11, height: 11 }} />}
                </button>
              </div>
            </div>

            <div style={{ padding: '5px 1px 0', flexShrink: 0 }}>
              <div ref={previewVideoContainerRef} style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000' }}>
                <video ref={deckPreviewVideoRef} autoPlay playsInline muted={previewMuted}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                {(!deckPreviewSourceId || !previewHasSignal) && (
                  <div style={{ position: 'absolute', inset: 0, background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <VideoOff style={{ width: '35%', height: '35%', opacity: 0.18, color: '#fff' }} />
                    {!deckPreviewSourceId && <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.18)' }}>Sélectionner une source</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* fin COL 2 */}

          {/* ─── COL 3 : Flux final ─── */}
          <div style={{
            display: 'flex', flexDirection: 'column',
            border: '1.5px solid #ef4444', borderRadius: 7,
            overflow: 'hidden', background: 'rgba(0,0,0,0.3)',
            // Le cadre du tally est posé en surimpression (voir plus bas) : la
            // bordure garde ses 1.5 px, sans quoi l'image du flux final ne serait
            // plus alignée sur celle du preview, juste à côté.
            position: 'relative',
            ...(inDetachedTab ? { gridColumn: 2, gridRow: 1 } : {}),
          }}>
            <div style={colHeaderStyle}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', boxShadow: streamSource === 'webrtc' ? '0 0 5px #ef4444' : 'none', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', fontFamily: 'monospace', flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {(() => {
                  const src   = availableSources.find(s => s.id === selectedVideoSource);
                  const label = src ? cleanLabel(resolveSourceLabel(src, sourceAliases)) : null;
                  return label ? `Final — ${label}` : 'Flux final';
                })()}
              </span>
              {streamSource === 'webrtc' && (
                <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.8px', color: '#ef4444', animation: 'mvPulse 1.5s ease-in-out infinite', flexShrink: 0 }}>● LIVE</span>
              )}
              <button onClick={onToggleFinalMuted} title={finalMuted ? 'Activer le son flux final' : 'Couper le son flux final'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: finalMuted ? 'rgba(255,255,255,0.35)' : '#f87171', display: 'flex', alignItems: 'center' }}>
                {finalMuted ? <VolumeX style={{ width: 11, height: 11 }} /> : <Volume2 style={{ width: 11, height: 11 }} />}
              </button>
            </div>
            <div style={{ padding: '5px 1px 0', flexShrink: 0 }}>
              <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000' }}>
                <video ref={canvasPreviewRef} autoPlay playsInline muted
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                {!canvasHasSignal && (
                  <div style={{ position: 'absolute', inset: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <VideoOff style={{ width: '35%', height: '35%', opacity: 0.18, color: '#fff' }} />
                  </div>
                )}
              </div>
            </div>
            <div style={{
              position     : 'absolute',
              inset        : 0,
              border       : `${TALLY_WIDTH}px solid #ef4444`,
              borderRadius : 7,
              pointerEvents: 'none',
              zIndex       : 3,
            }} />
          </div>
          {/* fin COL 3 */}

        </div>
        {/* fin grille 3 colonnes */}

        {withChat && (
          <aside style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10, padding: '6px 6px 6px 0' }}>
            <KleonLogo responsive style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <DetachedChatFeed channelId={broadcastingChannel.id} channelName={broadcastingChannel.name} />
            </div>
          </aside>
        )}
        </div>

        <style>{`
          @keyframes mvPulse {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.45; }
          }
        `}</style>

      </div>

      {isSettingsOpen && (
        <ConfigModal
          slotConfigs={slotConfigs}
          availableSources={availableSources}
          layoutSlots={layoutSlots}
          sourceAliases={sourceAliases}
          closeOnFullscreen={closeOnFullscreen}
          onToggleCloseOnFullscreen={onToggleCloseOnFullscreen}
          onSlotConfigsChange={onSlotConfigsChange}
          onClose={() => setIsSettingsOpen(false)}
        />
      )}
    </>
  );

}) as React.FC<MultiviewPanelV2Props>;

export default MultiviewPanelV2;


//		  logo        = {broadcastingChannel ? getLogoImageUrl(broadcastingChannel) : ''}
